import { Fragment, useCallback, useEffect, useMemo, useState  } from "react";
import { api } from "../../api/client";
import { fmtLocalDate } from "../../utils/datetime";
import "../accounting/accounting.css"; // .acc-tabs / .acc-tab — the Department detail tab strip
import { AppDataGrid, gridExportMatrix, type AppColumn } from "../../components/AppDataGrid";
import { LABEL, SURROGATE_KEYS, INTERNAL_STANDARD_KEYS, QC_KEYS } from "./testCodeTabColumns";
import { AppDialog } from "../../components/AppDialog";
import { useNotify } from "../../components/notifications";
import {
  QC_FIELD, QC_KIND, qcKindOf, missingIn, effValue, isRetired, type QcKind, type QcParamRow,
} from "./testCodeQc";
import { ExportMenu } from "../../components/AppToolbar";
import { AiCombo } from "../../components/AiCombo";
import { TestRoundingDialog } from "./TestRoundingDialog";
import { TestComponentDialog, type ComponentRow } from "./TestComponentDialog";
import { TestCodeGroupTest } from "./TestCodeGroupTest";
import { useConfirm } from "../../components/confirm";
import {
  QcTypeAddDialog,
  TestGuideDialog,
  PrepMethodDialog,
  type GuideRow,
  type PrepRow,
} from "./TestCodeMethodDefaults";

/**
 * Test Code → the two child cards on the EDIT detail view (prototype v12):
 * **Method Defaults** (QC Types · Test Guide · Prep Method) and **Test Parameter
 * Defaults** (Sample Parameters · Surrogates · Internal Standards · QC Limits ·
 * Component · QC Parameter Defaults), plus the setup checklist strip above them.
 *
 * READ-ONLY for now. Every tab shows real data through the shared
 * `<AppDataGrid>`, so sorting, column filters, Export and Maximize come for
 * free; the row actions (+ Add / Remove / Retire / Reactivate) are the next
 * slice and are deliberately absent rather than present-but-dead.
 *
 * The Sample Parameters tab carries the prototype's column-group chips —
 * Reporting (8) · Limits (9) · Rounding (4) · Other (4) — so all 29 columns are
 * reachable without a 29-column wall by default.
 *
 * Shown only once the record is SAVED: a test with no id has no children to
 * read, and the NEW form shows the Copy-from panel in this slot instead.
 */

type Row = Record<string, unknown>;

const G = {
  rep: ["Reporting", ["defaultResult", "defaultUnits", "finalDefaultResult", "finalDefaultUnits", "rl", "mdl", "mdlSpikeAmount", "mdlSpikeUnits"]],
  lim: ["Limits", ["rsd", "regulatoryLimit", "mcl", "loq", "uql", "lcLimit", "ucLimit", "reLcLimit", "reUcLimit"]],
  rnd: ["Rounding", ["sigFig", "cutOff", "decimal"]],
  oth: ["Other", ["synonym", "accreditation", "comment", "isResultDefaultValue", "retireDate"]],
} as const;

/** Band colour per group, read VERBATIM from the prototype stylesheet
 *  (Test-Code-v12.html line 183), not from memory:
 *
 *    .b-rep #2f6db0 (blue)   .b-lim #d98a1c (amber)
 *    .b-rnd #7c4dce (violet) .b-oth #9aa3af (grey)
 *
 *  The colour is the rule ABOVE the band, not the label — every band label is
 *  the same grey (#4b5563) over a #fafbfc strip. An earlier pass had Reporting
 *  and Limits both on the brand orange and left Other uncoloured, which was
 *  simply wrong about what the prototype does. */
const G_COLOR: Record<keyof typeof G, string> = {
  rep: "#2f6db0",
  lim: "#d98a1c",
  rnd: "#7c4dce",
  oth: "#9aa3af",
};

/* LABEL, and each tab's column ORDER, live in testCodeTabColumns.ts so the
   grids and the GoLive parity test read ONE list (2026-10-30). */
export { LABEL } from "./testCodeTabColumns";

/** Component tab columns — the prototype's set, now that the tab reads the real
 *  dbo.Component row rather than distinct names off the parameters. */
const COMP_COLS: AppColumn<Row>[] = [
  {
    field: "component",
    header: "Components",
    width: 180,
    render: (r: Row) =>
      r.isShared ? (
        <>
          {String(r.component ?? "")}
          <span
            className="tc-shared-badge"
            title="Shared with other tests — edit or remove it where it is owned."
          >
            shared
          </span>
        </>
      ) : (
        String(r.component ?? "")
      ),
  },
  {
    /* "5 — Benzene, Toluene, …" — the count first, as the prototype prints it
       (COLS.comp params fmt, v12:607). The list is truncated by the column
       width, so without the number you cannot tell a component of 5 from one
       of 50. parameterCount comes from AI_TestMethod_Components_SP, which has
       always returned it; only the cell was leaving it out. */
    field: "parameters", header: "Parameters", width: 260,
    text: (r) => {
      const names = String(r.parameters ?? "");
      const n = Number(r.parameterCount ?? 0);
      return n ? `${n} — ${names}` : names;
    },
  },
  { field: "comment", header: "Comment", width: 160 },
  { field: "retireDate", header: "RetireDate", type: "date", width: 110 },
  { field: "lastUpdatedDate", header: "LastUpdatedDate", type: "date", width: 140 },
  { field: "lastUpdatedBy", header: "LastUpdatedBy", width: 140 },
];

/** The columns "Fill … = …" may set. This list MIRRORS the server whitelist in
 *  AI_TestMethod_QcFill_SP — the server is authoritative and rejects anything
 *  else, so a drift here is a missing option, never a bad write. */
const FILL_COLS: ReadonlyArray<readonly [string, string]> = [
  ["RL", "RL"], ["MDL", "MDL"], ["UCLimit", "UCLimit"], ["RPDCtrlLimit", "RPDCtrlLimit"],
  ["SpikeAmount", "SpikeAmount"], ["RecLCLimit", "RecLCLimit"], ["RecUCLimit", "RecUCLimit"],
  ["SigFig", "SigFig"], ["CutOff", "CutOff"], ["Decimal", "Decimal"], ["Comment", "Comment"],
] as const;


/* ── INLINE CELL EDITING (prototype v12 cellHTML :642) ────────────────────────
   In the prototype a grid cell IS an input unless the column is read-only or
   the row is retired, which is how a limit gets typed where it is read. These
   two maps say which of AI's columns that applies to and what the API calls
   them.

   The column VOCABULARY is AI_TestMethod_QcFill_SP's, so a typed cell and the
   Fill bar name the same thing; the legacy column each maps to lives in
   AI_TestMethod_ParamCells_SP and nowhere else.

   NOT HERE, deliberately: parameter (the row's identity — Add / Remove own it),
   retireDate (Retire / Reactivate own it), component (its own tab), and every
   derived column (qcRole, inherited values, counts). Notation is in the
   prototype's Rounding band but there is NO such column on dbo.Testparameter,
   so it is absent here exactly as it is absent from the grid. */
const CELL_COL: Record<string, string> = {
  sort: "Sort",
  defaultResult: "DefaultResult", finalDefaultResult: "FinalDefaultResult",
  rl: "RL", mdl: "MDL", mdlSpikeAmount: "MDLSpikeAmount",
  rsd: "RSD", regulatoryLimit: "RegulatoryLimit", mcl: "MCL", loq: "LOQ", uql: "UQL",
  lcLimit: "LCLimit", ucLimit: "UCLimit", reLcLimit: "RELCLimit", reUcLimit: "REUCLimit",
  sigFig: "SigFig", cutOff: "CutOff", decimal: "Decimal",
  synonym: "Synonym", accreditation: "Accreditation", comment: "Comment",
  surrogateAmount: "SurrogateAmount", surrogateLowLimit: "SurrogateLowLimit",
  surrogateHighLimit: "SurrogateHighLimit",
  spikeAmount: "SpikeAmount",
  rpdCtrlLimit: "RPDCtrlLimit", rpdLcLimit: "RPDLCLimit",
  recLcLimit: "RecLCLimit", recUcLimit: "RecUCLimit",
  rpdSigFig: "RPDSigFig", rpdCutOff: "RPDCutOff", rpdDecimal: "RPDDecimal",
  recSigFig: "RECSigFig", recCutOff: "RECCutOff", recDecimal: "RECDecimal",
};

/** The cells that are a PICK, not typing. Each stores a key, so the editor
 *  offers names and the handler sends the id. "surr" / "istd" are this test's
 *  OWN surrogate / internal-standard rows (SurrogateLink points at a
 *  Testparameter, not a Parameter), "unit" is the shared dbo.Unit master. */
const CELL_FK: Record<string, { col: string; src: "unit" | "surr" | "istd" }> = {
  surrogate:         { col: "SurrogateLink",        src: "surr" },
  internalStandard:  { col: "InternalStandardLink", src: "istd" },
  defaultUnits:      { col: "DefaultUnits",         src: "unit" },
  finalDefaultUnits: { col: "FinalDefaultUnits",    src: "unit" },
  mdlSpikeUnits:     { col: "MDLSpikeUnits",        src: "unit" },
  surrogateUnits:    { col: "SurrogateUnits",       src: "unit" },
  spikeUnits:        { col: "SpikeUnits",           src: "unit" },
};

/** Cells the server stores as a number, so the editor offers a number field and
 *  refuses letters before the round trip. */
const CELL_NUM = new Set(["sort", "mdlSpikeAmount", "surrogateAmount", "spikeAmount"]);

const col = (f: string, w?: number): AppColumn<Row> => ({
  field: f,
  header: LABEL[f] ?? f,
  type: f.toLowerCase().endsWith("date") ? "date" : "text",
  ...(w ? { width: w } : {}),
  // A stored yes/no, read-only here: the box says what the word "false" did.
  ...(f === "isResultDefaultValue"
    ? {
        render: (r: Row) => (
          <input type="checkbox" checked={!!r[f]} disabled aria-label={`${LABEL[f] ?? f}: ${r[f] ? "yes" : "no"}`} />
        ),
        text: (r: Row) => (r[f] ? "true" : "false"),
      }
    : {}),
});

/* GoLive's QC Parameter Defaults set (Testparameter_ListView_Test_QCSampleParameter_QCParameterDefault,
   27 visible) plus QcRole, which AI shows and legacy does not — it is derived
   from the QC type and reads usefully beside it. Twelve of these were missing
   until 2026-10-30; all were already columns on dbo.Testparameter. */
const QC_COLS: AppColumn<Row>[] = QC_KEYS.map((k) =>
  col(k, k === "qcType" ? 150 : k === "parameter" ? 180 : k === "qcRole" ? 100
       : k === "synonym" ? 120 : k === "sort" ? 60 : k === "comment" ? 140 : undefined));

/** The Method Defaults column sets. Declared once because the GRID and its
 *  "Export to" both read them — two copies could drift into exporting columns
 *  the grid does not show. */
const MD_COLS = {
  qc: [col("qcType", 170), col("qcRole", 110), col("qcRootRole", 120),
       col("qcSource", 130), col("sort", 60), col("excludeReport", 110)],
  /* Container is the widest VALUE on this tab ("500 mL, Plastic/Glass") while
     Preservative and Temperature hold a word and a range, so it takes the
     larger share (owner request 2026-10-22). Under fixedLayout these numbers
     are PROPORTIONS of what the pinned tick + pencil columns leave, not pixels:
     280/170/120 gives Container roughly half the grid. */
  guide: [col("container", 280), col("preservative", 170), col("temperature", 120)],
  prep: [col("method", 150), col("tier", 60), col("prepType", 150), col("department", 130),
         col("instrument", 130), col("description", 180), col("sort", 60),
         col("activeDate", 110), col("retireDate", 110)],
} as const;

/* ── ONE FETCH PER URL WHILE ONE IS IN FLIGHT ─────────────────────────────────
   The detail page mounts TWO components from this file — <TestCodeSetupStrip>
   (SettingsDetailPage's headerSlot) and <TestCodeDetailTabs> — and they are
   siblings, so they cannot share state through props. Each called useRows for
   the SAME urls, so opening a test fetched qc-types, test-guide, prep-methods,
   parameters?kind=Sample, qc-parameters AND the record itself TWICE.

   That is cheap on a small test and expensive on a big one: VOC / EPA 524.2
   carries 174 sample-parameter rows over an 88-column projection and 638 QC
   rows, so the two heaviest payloads on the page were each sent twice (owner
   report 2026-10-22, "it takes time to load the data for the VOC test").

   ⚠️ THE SERVER WAS NEVER THE PROBLEM — measured on the live database for that
   test, every procedure answers in 0–15 ms. The cost is the payload and the
   round trips, so this is where it is fixed.

   IN-FLIGHT ONLY, deliberately: the entry is dropped the moment the request
   settles, so a later load always hits the network and nothing can be served
   stale. The key carries the reload token, so a Refresh after a save never
   joins the pre-save request. */
const inFlight = new Map<string, Promise<Row[]>>();

function fetchRowsOnce(url: string, token: number): Promise<Row[]> {
  const key = `${token}|${url}`;
  const pending = inFlight.get(key);
  if (pending) return pending;
  const run = api
    .get(url)
    .then(({ data }) => (Array.isArray(data?.data) ? (data.data as Row[]) : []))
    .finally(() => { inFlight.delete(key); });
  inFlight.set(key, run);
  return run;
}

function useRows(url: string | null, token = 0) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!url) {
      setRows([]);
      return;
    }
    let alive = true;
    setLoading(true);
    void (async () => {
      try {
        const data = await fetchRowsOnce(url, token);
        if (alive) setRows(data);
      } catch {
        // One failing tab must not take the detail page down; the shared axios
        // error handling already surfaced it.
        if (alive) setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url, token]);
  return { rows, loading };
}

/** Which section a setup step jumps to. */
export type TestCodeJump = "gen" | "sp" | "qc" | "qcl" | "guide" | "prep";

/* The strip sits in SettingsDetailPage's headerSlot and the tabs are a sibling
   component, so they cannot share state through props. Both live in THIS file,
   so a module-scoped subscriber set keeps the wiring local instead of wrapping
   the whole detail page in a context for one strip. */
const jumpSubs = new Set<(k: TestCodeJump) => void>();

/** Scroll a section into view and flash it, as the prototype does (v12 line
 *  938). Runs after the tab switch has painted, so the panel being revealed is
 *  the one that ends up on screen. */
function scrollToSection(sel: string) {
  requestAnimationFrame(() => {
    const el = document.querySelector<HTMLElement>(sel);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    el.classList.add("tc-flash");
    window.setTimeout(() => el.classList.remove("tc-flash"), 1300);
  });
}

/** The prototype's setup strip: one chip per section, ✓ when it is configured
 *  and ! when it still needs attention. Counts come from the tabs themselves,
 *  so the strip can never disagree with the grids below it. Each chip is a
 *  BUTTON that jumps to its section — the prototype's data-jump (v12 line 776). */
function SetupStrip({
  items,
}: {
  items: { label: string; ok: boolean; detail: string; jump: TestCodeJump }[];
}) {
  return (
    <div className="tc-setup">
      {items.map((i, n) => (
        <Fragment key={i.label}>
          {n > 0 && <span className="tc-setup-line" />}
          <button
            type="button"
            className={`tc-setup-item${i.ok ? " ok" : " warn"}`}
            title={`Go to ${i.label}`}
            onClick={() => {
              // The tabs listen and switch to the right one; "gen" needs no
              // switch, so the strip scrolls there itself.
              jumpSubs.forEach((f) => f(i.jump));
              if (i.jump === "gen") scrollToSection(".tc-detail .sd-section-h");
            }}
          >
            <span className="tc-setup-ic">{i.ok ? "✓" : "!"}</span>
            <span>
              <b>{i.label}</b>
              <small>{i.detail}</small>
            </span>
          </button>
        </Fragment>
      ))}
    </div>
  );
}

/**
 * The setup checklist strip. Mounted in SettingsDetailPage's headerSlot so it
 * sits directly under the title, above the action bar, as the prototype places
 * it. It owns a SECOND, lightweight read of the same tabs rather than sharing
 * state with the cards below: the alternative was a context wrapping the whole
 * detail page for one strip, and these are small GETs the browser caches for the
 * few seconds between the two mounts.
 */
export function TestCodeSetupStrip({ id }: { id: string }) {
  const base = `/api/v1/test-methods/${id}`;
  /* The four resources the shared Test Method dialog also serves
     (TestMethodTabsController) live under /test-code/ so the two
     forms cannot claim the same route. Everything else - test-guide,
     qc-types, qc-parameters, the *-cells writers - is ours alone and
     stays on `base`. */
  const tcBase = `${base}/test-code`;
  const qcTypes = useRows(`${base}/qc-types`);
  const guides = useRows(`${base}/test-guide`);
  const preps = useRows(`${tcBase}/prep-methods`);
  const sample = useRows(`${tcBase}/parameters?kind=Sample`);
  const qcParams = useRows(`${base}/qc-parameters`);

  // The General step's caption is the test's METHOD (prototype v12), or "Group
  // test" when it has none because it is a group — the prototype's own rule.
  // This used to come from an optional `method` prop that the only call site
  // never passed, so the chip always read "—"; the strip reads the test itself
  // instead, which cannot fall out of step with the record on screen.
  const [general, setGeneral] = useState<{ method?: string; isGroup?: boolean; ok: boolean }>({ ok: true });
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.get(base);
        const d = data?.data ?? {};
        if (alive) {
          setGeneral({
            method: d.methodName ?? undefined,
            isGroup: !!d.isGroup,
            // Same condition the prototype uses: the step is complete once the
            // test has both a matrix and a name.
            ok: !!d.matrixId && !!String(d.testName ?? "").trim(),
          });
        }
      } catch {
        if (alive) setGeneral({ ok: true });
      }
    })();
    return () => {
      alive = false;
    };
  }, [base]);

  /* QC Limits, as the prototype computes it (setupHTML + totalMissing, v12):
       miss  = required limit VALUES still empty, across every QC type
       unset = live sample parameters not on a QC type at all
     The step is WARN when there are no QC types or anything is missing, and its
     caption names whichever shortfall is bigger news. */
  const qcHealth = useMemo(() => {
    const key = (r: { parameterId?: unknown; parameter?: unknown }) =>
      String(r.parameterId ?? r.parameter ?? "");
    const live = [...new Set(sample.rows.filter((r) => !isRetired(r)).map((r) => key(r)))];
    const retiredKeys = new Set(sample.rows.filter((r) => isRetired(r)).map((r) => key(r)));

    const byType = new Map<string, QcParamRow[]>();
    for (const r of qcParams.rows as QcParamRow[]) {
      const id = String(r.qcTypeId ?? r.qcType ?? "");
      if (!id) continue;
      if (!byType.has(id)) byType.set(id, []);
      byType.get(id)!.push(r);
    }

    let miss = 0;
    let unset = 0;
    for (const t of qcTypes.rows) {
      const rows = byType.get(String(t.id ?? "")) ?? [];
      const counted = rows.filter((r) => !retiredKeys.has(key(r)));
      miss += missingIn(
        counted,
        qcKindOf({
          qcRole: t.qcRole == null ? null : String(t.qcRole),
          qcRootRole: t.qcRootRole == null ? null : String(t.qcRootRole),
        }),
      );
      const have = new Set(rows.map((r) => key(r)));
      unset += live.filter((k) => !have.has(k)).length;
    }
    return { miss, unset };
  }, [qcTypes.rows, qcParams.rows, sample.rows]);

  const activeSample = sample.rows.filter((r) => !isRetired(r)).length;
  const retiredSample = sample.rows.length - activeSample;

  return (
    <SetupStrip
      items={[
        {
          label: "General",
          ok: general.ok,
          detail: general.isGroup ? "Group test" : general.method || "—",
          jump: "gen",
        },
        {
          label: "Sample Parameters",
          ok: activeSample > 0,
          detail: `${activeSample} active${retiredSample ? ` · ${retiredSample} retired` : ""}`,
          jump: "sp",
        },
        { label: "QC Types", ok: qcTypes.rows.length > 0, detail: `${qcTypes.rows.length} active`, jump: "qc" },
        {
          label: "QC Limits",
          ok: qcTypes.rows.length > 0 && qcHealth.miss === 0,
          detail: qcHealth.miss
            ? `${qcHealth.miss} limits missing`
            : qcHealth.unset
              ? `${qcHealth.unset} slots not set up`
              : "complete",
          jump: "qcl",
        },
        { label: "Test Guide", ok: guides.rows.length > 0, detail: guides.rows.length ? `${guides.rows.length} container${guides.rows.length === 1 ? "" : "s"}` : "no container", jump: "guide" },
        { label: "Prep Method", ok: true, detail: preps.rows.length ? `${preps.rows.length} step(s)` : "none (optional)", jump: "prep" },
      ]}
    />
  );
}

export function TestCodeDetailTabs(
  { id, testName, viewOnly = false, reloadToken = 0, onGuideRowsChange }: {
    id: string;
    testName?: string;
    viewOnly?: boolean;
    /** Bumped after a header Save: Is Group can change which sections show. */
    reloadToken?: number;
    /** Tells the form whether a Test Guide row now exists, which is what unlocks
     *  the holding-time fields without losing anything typed in the form. */
    onGuideRowsChange?: (has: boolean) => void;
  },
) {
  const [tick, setTick] = useState(0);
  const reload = () => setTick((n) => n + 1);
  useEffect(() => {
    if (reloadToken) setTick((n) => n + 1);
  }, [reloadToken]);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [addOpen, setAddOpen] = useState(false);
  /** `oids` is set only when Retire was reached from the Remove escalation,
   *  where it carries the rows that are IN USE rather than everything ticked. */
  const [retireOpen, setRetireOpen] =
    useState<null | { reactivate: boolean; oids?: string[]; names?: string[]; inUse?: boolean }>(null);
  const notify = useNotify();
  // prompt comes from the SHARED dialog, never window.prompt (§18.6).
  const { confirm, prompt, alert } = useConfirm();
  const [mdTab, setMdTab] = useState<"qc" | "guide" | "prep">("qc");
  const [pdTab, setPdTab] = useState<"sp" | "surr" | "is" | "qcl" | "comp" | "qcd">("sp");
  const pickPdTab = (k: "sp" | "surr" | "is" | "qcl" | "comp" | "qcd") => {
    setPdTab(k);
    setRoSel(new Set());
  };

  // A setup-strip chip jumps here: switch to the tab it names, then scroll the
  // section into view and flash it. Subscribing in an effect (not on every
  // render) keeps exactly one listener per mount.
  useEffect(() => {
    const onJump = (k: TestCodeJump) => {
      if (k === "qc" || k === "guide" || k === "prep") {
        setMdTab(k);
        setMdSel(new Set());
        scrollToSection("#tc-sec-md");
      } else if (k === "sp" || k === "qcl") {
        setPdTab(k);
        setRoSel(new Set());
        scrollToSection("#tc-sec-pd");
      }
    };
    jumpSubs.add(onJump);
    return () => { jumpSubs.delete(onJump); };
  }, []);
  const [groups, setGroups] = useState<Set<keyof typeof G>>(
    () => new Set(Object.keys(G) as (keyof typeof G)[]),
  );

  // Method Defaults selection + dialogs. Selection is PER TAB (`mdSel`) and is
  // cleared whenever the tab changes, so an Edit cannot act on a row the user
  // ticked on a different tab and can no longer see.
  const [mdSel, setMdSel] = useState<Set<string>>(new Set());
  // Selection for the three READ-ONLY Test Parameter Defaults tabs (QC Limits ·
  // Component · QC Parameter Defaults). Kept apart from `sel`, which the row
  // toolbar reads: these tabs have no toolbar yet, so their ticks must not leak
  // into a Remove/Retire that would then act on rows the user cannot see.
  const [roSel, setRoSel] = useState<Set<string>>(new Set());
  const roSelect = {
    selectable: true as const,
    selectedIds: roSel as Set<string | number>,
    onSelectionChange: (ids: Iterable<string | number>) => setRoSel(new Set([...ids].map(String))),
  };
  const [qcAddOpen, setQcAddOpen] = useState(false);
  const [roundOpen, setRoundOpen] = useState(false);
  const [compDlg, setCompDlg] = useState<null | { row: ComponentRow | null }>(null);
  // QC Parameter Defaults bar: which QC type is in view, and the Fill pair.
  const [qcFilter, setQcFilter] = useState("");
  // QC Limits view: the prototype's two modes (v12) — a rail of QC types with
  // only that KIND's limit columns, or the flat matrix of every row.
  const [qcView, setQcView] = useState<"type" | "matrix">("type");
  const [qcSel, setQcSel] = useState("");
  /** null = the chip has not been touched, so each grid decides for itself
   *  (see `revealed`); true/false is the user's explicit choice and wins
   *  everywhere. */
  const [retiredPref, setRetiredPref] = useState<boolean | null>(null);
  /** The QC grids carry their own all-retired handling, so they read the plain
   *  preference and still default to hidden — unchanged by the rule below. */
  const showRetired = retiredPref ?? false;
  /** Bumped only when retired rows are REVEALED, so the grids jump to them. */
  const [retiredJump, setRetiredJump] = useState(0);
  /* A tab whose rows are ALL retired used to render an EMPTY grid under a tab
     badge saying it held one, with nothing on screen to say where the row had
     gone (owner report 2026-10-30: Surrogates and Internal Standards both
     badged 1 over 0 rows). Until the user chooses otherwise, such a tab reveals
     its retired rows and the chip reads "Hide retired". A tab that still has a
     live row is untouched — hiding retired rows is right there. */
  const revealed = (rows: Row[]) =>
    retiredPref ?? (rows.length > 0 && rows.every((r) => isRetired(r)));
  const toggleRetired = (rows: Row[] = []) => {
    const next = !revealed(rows);
    if (next) setRetiredJump((n) => n + 1);
    setRetiredPref(next);
  };
  const [fillCol, setFillCol] = useState("RL");
  const [fillVal, setFillVal] = useState("");
  const [guideDlg, setGuideDlg] = useState<null | { row: GuideRow | null }>(null);
  const [prepDlg, setPrepDlg] = useState<null | { row: PrepRow | null }>(null);

  const base = `/api/v1/test-methods/${id}`;
  /* The four resources the shared Test Method dialog also serves
     (TestMethodTabsController) live under /test-code/ so the two
     forms cannot claim the same route. Everything else - test-guide,
     qc-types, qc-parameters, the *-cells writers - is ours alone and
     stays on `base`. */
  const tcBase = `${base}/test-code`;
  const qcTypes = useRows(`${base}/qc-types`, tick);
  const guides = useRows(`${base}/test-guide`, tick);
  const hasGuideRows = guides.rows.length > 0;
  useEffect(() => {
    if (!guides.loading) onGuideRowsChange?.(hasGuideRows);
    // onGuideRowsChange is a fresh closure each render; only the answer matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guides.loading, hasGuideRows]);
  const preps = useRows(`${tcBase}/prep-methods`, tick);
  const sample = useRows(`${tcBase}/parameters?kind=Sample`, tick);
  const surr = useRows(`${tcBase}/parameters?kind=Surrogate`, tick);
  const istd = useRows(`${tcBase}/parameters?kind=Internal`, tick);
  // One fetch serves both QC tabs: QC Limits is this set grouped by QC type and
  // QC Parameter Defaults is the same rows as one sheet, so fetching twice would
  // double the cost of the heaviest tab (638 rows on the largest test).
  const qcParams = useRows(`${base}/qc-parameters`, tick);

  // The Component tab reads dbo.Component, not the distinct names on the
  // parameter rows: a derived list could show a name but never its comment,
  // retire date or audit stamps, and there was nothing to edit.
  const comps = useRows(`${tcBase}/components`, tick);

  /* A test with Is Group ticked carries MEMBERS, not parameters, and GoLive
     shows them in place of Method Defaults / Test Parameter Defaults
     (Model.xafml:67464). The flag comes back with the rows, so this is the same
     one call the Group Test card would make anyway. */
  const [isGroup, setIsGroup] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.get(`${tcBase}/group-tests`);
        if (alive) setIsGroup(!!data?.data?.isGroup);
      } catch {
        // Unknown reads as "not a group": the ordinary sections are the safe
        // default, and the shared axios handling already surfaced the failure.
        if (alive) setIsGroup(false);
      }
    })();
    return () => { alive = false; };
  }, [base, tick]);
  const components = comps.rows;

  // The owning test — for the Prep Method dialog's read-only Department and
  // its Method default. One small GET; the browser serves it from cache for
  // the life of the page.
  const [owner, setOwner] = useState<{ methodId?: string; department?: string } | null>(null);
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.get(base);
        const d = data?.data ?? {};
        if (alive) setOwner({ methodId: d.methodId, department: d.department ?? d.departmentName });
      } catch {
        if (alive) setOwner(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [base]);

  /* ── INLINE CELL EDITING ──────────────────────────────────────────────────
     The units behind every *Units cell. One fetch for the page; the list is a
     small master, so it is not re-read per grid. */
  const [units, setUnits] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.get("/api/v1/test-methods/lookups/unit");
        const rows = (data?.data ?? []) as { id?: string; oid?: string; name?: string }[];
        if (alive) {
          setUnits(rows.map((r) => ({ id: String(r.id ?? r.oid ?? ""), name: String(r.name ?? "") })));
        }
      } catch {
        // A combo with no options stays read-only rather than offering a pick
        // that cannot be saved; the shared axios handling already surfaced it.
        if (alive) setUnits([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  /** Name → id for a pick cell. The grid shows names (it is what the reader
   *  returns), the API stores the key. */
  const fkList = useCallback(
    (src: "unit" | "surr" | "istd") =>
      src === "unit"
        ? units
        : (src === "surr" ? surr.rows : istd.rows).map((r) => ({
            id: String(r.oid ?? ""), name: String(r.parameter ?? ""),
          })),
    [units, surr.rows, istd.rows],
  );

  /* A cell commits on blur / Enter, IMMEDIATELY — the one deliberate deviation
     from the prototype, which buffers every edit into its working copy and
     writes them on the page Save (v12:1035 markDirty).
     Reason: in AI these tabs are NOT part of the header's save payload. Every
     other action in them — Add, Remove, Retire, Fill, the popups — already
     writes on click, and the Save trio belongs to the record header above. A
     buffered cell would be the only thing on the page waiting for a Save that
     does not collect it, so an RL typed and then clicked away from would be
     lost silently — the very thing standing rule 15 exists to prevent.
     The endpoint takes a LIST, so moving to a buffered model later needs no
     second endpoint. */
  const commitCell = useCallback(
    async (oid: string, column: string, value: string) => {
      if (!oid || !column) return;
      try {
        const { data } = await api.put(`${base}/parameter-cells`, {
          edits: [{ oid, column, value }],
        });
        // Only re-read when something really changed: the procedure returns an
        // empty set for a cell committed with the value it already held, and
        // reloading five grids for a no-op would make tabbing through a row
        // feel broken.
        if ((data?.data?.changed ?? 0) > 0) reload();
      } catch (e: unknown) {
        notify.error(
          (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
            "Could not save that cell.",
        );
        reload();   // put the cell back to what is stored
      }
    },
    // reload / notify are stable for the life of the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base],
  );

  /** Attach the shared grid's click-to-edit to every column that maps to a
   *  writable dbo.Testparameter column. A column not in either map is returned
   *  UNCHANGED, so a derived or identity column can never become editable by
   *  accident. */
  const editable = useCallback(
    (cols: AppColumn<Row>[]): AppColumn<Row>[] =>
      // VIEW mode: no inline editors anywhere. Returning the columns untouched
      // renders them as static text, the same path a retired row takes.
      viewOnly ? cols : cols.map((c) => {
        const fk = CELL_FK[c.field];
        if (fk) {
          const list = fkList(fk.src);
          if (!list.length) return c;
          return {
            ...c,
            edit: {
              type: "combo" as const,
              options: ["", ...list.map((o) => o.name)],
              clearable: true,
              // A retired row is read-only, exactly as the prototype renders it
              // (cellHTML returns static text when `ret`). The server refuses it
              // too — the UI is not the boundary.
              disabled: (r: Row) => isRetired(r),
            },
          };
        }
        if (!CELL_COL[c.field]) return c;
        return {
          ...c,
          edit: {
            type: CELL_NUM.has(c.field) ? ("number" as const) : ("text" as const),
            disabled: (r: Row) => isRetired(r),
          },
        };
      }),
    [fkList],
  );

  /** One commit handler for every parameter grid: they are all the same
   *  dbo.Testparameter row seen through different column sets. */
  const onCell = useCallback(
    (row: Row, c: AppColumn<Row>, v: string) => {
      const fk = CELL_FK[c.field];
      if (fk) {
        const text = v.trim();
        // Clearing is explicit: an EMPTY commit clears the key.
        if (text === "") { void commitCell(String(row.oid ?? ""), fk.col, ""); return; }
        const hit = fkList(fk.src).find((o) => o.name === text);
        // A value that is not in the list is NOT a clear. This used to send ""
        // when the name did not match, so a commit the combo could not resolve
        // silently WIPED the unit or the surrogate link instead of leaving it
        // alone. Reload instead, so the cell goes back to what is stored.
        if (!hit) { reload(); return; }
        void commitCell(String(row.oid ?? ""), fk.col, hit.id);
        return;
      }
      const column = CELL_COL[c.field];
      if (column) void commitCell(String(row.oid ?? ""), column, v);
    },
    [commitCell, fkList, viewOnly],
  );

  /* ── QC TYPES GRID — inline cells ─────────────────────────────────────────
     ⚠ TWO SCOPES, and the difference is the whole point:
       • ExcludeReport is per (test, QC type) — dbo.TestMethodQCTypeExclusion —
         so it touches THIS test only.
       • QC_Source and Sort live on dbo.QCType, the SHARED master, so changing
         one changes that QC type for EVERY test that uses it. The prototype
         edits them freely because its model is a per-test copy; AI's is not, so
         the header says so and the confirm says so again before it lands.
     Built on owner instruction 2026-10-22 after that consequence was stated. */
  const [qcSources, setQcSources] = useState<string[]>([]);
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.get("/api/v1/test-methods/lookups/qc-source");
        const rows = (data?.data ?? []) as { name?: string }[];
        if (alive) setQcSources(rows.map((r) => String(r.name ?? "")).filter(Boolean));
      } catch {
        // No options means the cell stays read-only rather than offering a
        // pick that cannot be saved.
        if (alive) setQcSources([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  const commitQcTypeCell = async (qcTypeOid: string, column: string, value: string) => {
    try {
      const { data } = await api.put(`${base}/qc-type-cells`, {
        edits: [{ qcTypeOid, column, value }],
      });
      if ((data?.data?.changed ?? 0) > 0) reload();
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
          "Could not save that cell.",
      );
      reload();
    }
  };

  /** A shared-master cell asks once before it lands — the prototype does not,
   *  because in its model the edit is this test's alone. */
  const onQcTypeCell = async (row: Row, c: AppColumn<Row>, v: string) => {
    const column = c.field === "qcSource" ? "QC_Source" : c.field === "sort" ? "Sort" : "";
    if (!column) return;
    const ok = await confirm({
      title: `Change ${column === "Sort" ? "Sort" : "QC Source"}`,
      message:
        `${row.qcType ?? "This QC type"} is shared: this changes it for EVERY test that uses it, ` +
        "not just this one. Continue?",
    });
    if (!ok) { reload(); return; }   // put the cell back
    await commitQcTypeCell(String(row.id ?? row.oid ?? ""), column, v);
  };

  /** ExcludeReport is this test's own flag, so it toggles on click with no
   *  confirm — the prototype's checkbox, through the endpoint that already
   *  existed for it (standing rule 8). */
  const toggleExcludeReport = async (row: Row) => {
    const next = !row.excludeReport;
    try {
      await api.patch(`${base}/qc-types/${String(row.id ?? row.oid ?? "")}/exclude-report`, {
        excludeReport: next,
      });
      reload();
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
          "Could not change ExcludeReport.",
      );
    }
  };

  /** MD_COLS.qc with the three editable cells wired. */
  const qcTypeCols = useMemo((): AppColumn<Row>[] =>
    MD_COLS.qc.map((c) => {
      if (c.field === "excludeReport") {
        return {
          ...c,
          render: (r: Row) => (
            <input
              type="checkbox"
              checked={!!r.excludeReport}
              // Not hidden like the tick column: this is stored data, so it stays
              // visible and readable - just not changeable.
              disabled={viewOnly}
              onClick={(e) => e.stopPropagation()}
              onChange={() => void toggleExcludeReport(r)}
              aria-label={`Exclude ${String(r.qcType ?? "")} from the report`}
            />
          ),
          text: (r: Row) => (r.excludeReport ? "true" : "false"),
        };
      }
      if (c.field === "qcSource" && qcSources.length) {
        return {
          ...c,
          headerText: "QC_Source",
          header: <span title="Shared master — changes this QC type on every test">QC_Source</span>,
          edit: { type: "combo" as const, options: ["", ...qcSources], clearable: true },
        };
      }
      if (c.field === "sort") {
        return {
          ...c,
          headerText: "Sort",
          header: <span title="Shared master — changes this QC type on every test">Sort</span>,
          edit: { type: "number" as const },
        };
      }
      return c;
    }),
    // commitQcTypeCell / toggleExcludeReport are stable for a given base.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qcSources],
  );

  /* The Export menus were building their whole row matrix inline, so every
     render of a tab rebuilt it — and on VOC / EPA 524.2 that is 638 QC rows x
     16 columns, twice on the QC Parameter Defaults tab. It ran again on every
     keystroke in the Fill box and after every committed cell, for an export
     nobody had asked for yet. Memoised on the rows and columns they read. */

  const spCols = useMemo(() => {
    // The prototype's four always-on columns (owner request 2026-10-13).
    // Surrogate / InternalStandard are Testparameter.SurrogateLink /
    // .InternalStandardLink — which surrogate or internal standard THIS sample
    // parameter is tied to — not the bits that sort rows onto the other tabs.
    // Component moved off: it is its own tab, and the prototype has no such
    // column here. The Component TAB still works, because the field is still on
    // the row; only the column is gone.
    const always = [
      col("parameter", 180),
      col("surrogate", 150),
      col("internalStandard", 160),
      col("sort", 60),
    ];
    const picked = (Object.keys(G) as (keyof typeof G)[])
      .filter((k) => groups.has(k))
      .flatMap((k) => (G[k][1] as readonly string[]).map((f) => col(f)));
    return [...always, ...picked];
  }, [groups]);

  // 4 always-on + the four toggleable groups.
  const totalCols = 4 + Object.values(G).reduce((a, g) => a + g[1].length, 0);

  /** The band row above Sample Parameters. The four always-on columns get one
   *  unlabelled leading band so every group lines up with its own columns;
   *  a group the user has toggled off contributes nothing. */
  const spBands = useMemo(() => {
    const out: { label: string; span: number; color?: string }[] = [{ label: "", span: 4 }];
    for (const k of Object.keys(G) as (keyof typeof G)[]) {
      if (!groups.has(k)) continue;
      out.push({ label: G[k][0], span: (G[k][1] as readonly string[]).length, color: G_COLOR[k] });
    }
    return out;
  }, [groups]);

  /* GoLive's own column sets, in GoLive's order — Testparameter_ListView_Test_Surrogates
     (23 visible, idx 0-22) and ..._InternalStandards (15 visible, idx 0-14).
     Both were short here: the Surrogates grid showed 9 of the 23 and Internal
     Standards 10, with two of each wearing another column's name, so values a
     lab had entered in XAF were simply not on screen (owner report
     2026-10-30). Every field was already in the table; most were already in
     the SP. */
  /* GoLive's own column sets, in GoLive's order — see testCodeTabColumns.ts.
     Built from the shared key lists so the grid and the parity test cannot
     disagree about what this tab shows. */
  const surrCols = useMemo(() => SURROGATE_KEYS.map((k) => col(k, k === "parameter" ? 190 : k === "sort" ? 60 : k === "comment" ? 150 : undefined)), []);
  const istdCols = useMemo(() => INTERNAL_STANDARD_KEYS.map((k) => col(k, k === "parameter" ? 190 : k === "sort" ? 60 : k === "comment" ? 150 : undefined)), []);

  // Which tabs support the row actions. QC Limits / QC Parameter Defaults need a
  // QC type chosen before Add knows where to put the rows, so they are read-only
  // until that picker exists - rendering a dead + Add would be worse.
  const kind: "Sample" | "Surrogate" | "Internal" | null =
    pdTab === "sp" ? "Sample" : pdTab === "surr" ? "Surrogate" : pdTab === "is" ? "Internal" : null;
  const activeRows: Row[] =
    pdTab === "sp" ? sample.rows : pdTab === "surr" ? surr.rows : pdTab === "is" ? istd.rows : [];
  /* Only rows that are ON SCREEN count as selected: with retired rows hidden, a
     tick left on one before it was hidden would otherwise let Remove / Retire
     act on a row the user cannot see. Reactivate still works — switch Show
     retired on, tick the row, reactivate. */
  const selectedInTab = [...sel].filter((x) =>
    (revealed(activeRows) ? activeRows : activeRows.filter((r) => !isRetired(r)))
      .some((r) => String(r.oid) === x));
  const activeCols = pdTab === "sp" ? spCols : pdTab === "surr" ? surrCols : istdCols;

  /** The single ticked component, if exactly one is ticked. */
  /** The row a plain click made CURRENT (G5 grids work this way). */
  const [compCur, setCompCur] = useState<string | null>(null);

  /** TICKED component, when exactly one is ticked. Destructive actions use this
   *  and nothing else: removing a row because it was merely clicked is how a
   *  component disappears by accident. */
  const compSel = (() => {
    if (roSel.size !== 1) return null;
    const [only] = [...roSel];
    return (components.find((r) => String(r.oid) === only) as ComponentRow | undefined) ?? null;
  })();

  /** What EDIT opens: the ticked row, else the clicked one, else the FIRST row.
   *  Every grid in the app opens with its first row active (standing rule 14),
   *  so Edit is usable the moment the tab appears instead of greyed until the
   *  user guesses that a row has to be clicked first (owner report 2026-10-27).
   *  Opening a component to look at it writes nothing, so a default target is
   *  safe here — Remove still demands an explicit tick. */
  const compEdit =
    compSel ??
    ((compCur
      ? (components.find((r) => String(r.oid) === compCur) as ComponentRow | undefined)
      : undefined) ??
      ((components[0] as ComponentRow | undefined) ?? null));

  /** The QC types present on this test's QC rows, for the filter dropdown. */
  const qcTypeOptions = useMemo(() => {
    const seen = new Map<string, { id: string; name: string; n: number }>();
    for (const r of qcParams.rows) {
      const id = String(r.qcTypeId ?? r.qcType ?? "");
      if (!id) continue;
      const e = seen.get(id) ?? { id, name: String(r.qcType ?? ""), n: 0 };
      e.n += 1;
      seen.set(id, e);
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [qcParams.rows]);

  const rowKey = (r: { parameterId?: unknown; parameter?: unknown }) =>
    String(r.parameterId ?? r.parameter ?? "");

  /* ── QC Limits "By QC type" ──────────────────────────────────────────────
     Per QC type: its kind, its rows, and how many required limits are still
     missing. The counts come from the SAME rows the grid shows, so the rail can
     never disagree with the pane beside it. */
  const activeSampleCount = useMemo(
    () => new Set(sample.rows.filter((r) => !isRetired(r)).map((r) => rowKey(r))).size,
    [sample.rows],
  );
  const qcTypeStats = useMemo(() => {
    const paramKey = rowKey;
    // Which sample parameters are retired, and which are live — the rail counts
    // against the LIVE ones, so a retired parameter never reads as "missing".
    const retiredParams = new Set(
      sample.rows.filter((r) => isRetired(r)).map((r) => paramKey(r)),
    );
    const liveParams = [
      ...new Set(sample.rows.filter((r) => !isRetired(r)).map((r) => paramKey(r))),
    ];

    const by = new Map<string, { id: string; name: string; kind: QcKind; rows: QcParamRow[] }>();
    for (const r of qcParams.rows as QcParamRow[]) {
      const id = String(r.qcTypeId ?? r.qcType ?? "");
      if (!id) continue;
      if (!by.has(id)) {
        by.set(id, { id, name: String(r.qcType ?? ""), kind: qcKindOf(r), rows: [] });
      }
      by.get(id)!.rows.push(r);
    }
    return [...by.values()]
      .map((t) => {
        // Counted the prototype's way: a row counts while its SAMPLE parameter
        // is live, not by the QC row's own retire date.
        const counted = t.rows.filter((r) => !retiredParams.has(paramKey(r)));
        const have = new Set(t.rows.map((r) => paramKey(r)));
        return {
          ...t,
          active: counted.length,
          retired: t.rows.length - counted.length,
          missing: missingIn(counted, t.kind),
          // Live sample parameters this QC type does not carry at all.
          notSetUp: liveParams.filter((k) => !have.has(k)).length,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [qcParams.rows, sample.rows]);

  // Keep a valid selection without fighting the user: only fall back to the
  // first type when the chosen one is gone (first load, or a type unlinked).
  useEffect(() => {
    if (!qcTypeStats.length) { if (qcSel) setQcSel(""); return; }
    if (!qcTypeStats.some((t) => t.id === qcSel)) setQcSel(qcTypeStats[0]!.id);
  }, [qcTypeStats, qcSel]);

  /** Total required limits still empty, across every QC type — the number the
   *  QC Limits TAB badge shows (prototype v12 totalMissing). */
  const totalMissing = useMemo(
    () => qcTypeStats.reduce((a, t) => a + t.missing, 0),
    [qcTypeStats],
  );

  const selStat = qcTypeStats.find((t) => t.id === qcSel) ?? null;

  /** Fill offers the columns THIS kind is checked against, with a * on the ones
   *  it requires (prototype v12). ⚠ Intersected with the server whitelist in
   *  AI_TestMethod_QcFill_SP, which writes 11 columns: the prototype also lists
   *  Units, SpikeUnits and the REC/RPD rounding triples, and offering those
   *  would hand the user an option the API rejects. The matrix view spans every
   *  kind, so it keeps the whole whitelist. */
  const fillOptions = useMemo(() => {
    const allowed = new Set(FILL_COLS.map(([k]) => k));
    if (!selStat) return FILL_COLS.map(([k, l]) => ({ value: k, label: l }));
    const kind = QC_KIND[selStat.kind];
    return kind.cols
      .map((c) => ({ key: c, header: QC_FIELD[c]?.header ?? c, req: kind.req.includes(c) }))
      .filter((o) => allowed.has(o.header))
      .map((o) => ({ value: o.header, label: o.req ? `${o.header} *` : o.header }));
  }, [selStat]);

  // A column the new list does not offer would otherwise stay selected and be
  // filled into a kind that never reads it.
  useEffect(() => {
    if (fillOptions.length && !fillOptions.some((o) => o.value === fillCol)) {
      setFillCol(fillOptions[0]!.value);
    }
  }, [fillOptions, fillCol]);
  // Hidden/shown on the SAMPLE parameter's retirement, the same basis the rail
  // and the footer count on - a QC row does not carry its own retirement here.
  const retiredParamKeys = useMemo(
    () => new Set(sample.rows.filter((r) => isRetired(r)).map((r) => rowKey(r))),
    [sample.rows],
  );
  const paneRows = useMemo(() => {
    if (!selStat) return [];
    const live = selStat.rows.filter((r) => !retiredParamKeys.has(rowKey(r)));
    if (!showRetired) return live;
    // Retired last, as on the parameter grids.
    return [...live, ...selStat.rows.filter((r) => retiredParamKeys.has(rowKey(r)))];
  }, [selStat, showRetired, retiredParamKeys]);

  /** Only the limit columns this KIND uses (prototype QK.cols) — a Blank has no
   *  spike or %Rec columns, so showing them invites filling a value the rule
   *  never reads. An INHERITED value renders grey italic, as it does in the
   *  legacy grid, so an override is distinguishable from what it inherits. */
  const kindCols = useMemo((): AppColumn<Row>[] => {
    if (!selStat) return [];
    return [
      { field: "parameter", header: "Parameter", width: 180 },
      ...QC_KIND[selStat.kind].cols.map((k) => {
        const f = QC_FIELD[k]!;
        return {
          field: f.field,
          header: f.header,
          width: k === "cmt" ? 150 : 95,
          render: (row: Row) => {
            const r = row as QcParamRow;
            const own = r[f.field];
            if (own !== null && own !== undefined && String(own).trim() !== "") return String(own);
            const inh = effValue(r, k);
            return inh ? <span className="tc-qc-inh">{inh}</span> : "";
          },
          // The editor REPLACES `render`, so the inherited value rides as the
          // placeholder — otherwise the "this comes from the Sample Parameter"
          // cue would vanish the moment the column became typeable. Clearing a
          // cell therefore falls back to the inherited value, which is what the
          // prototype means by "clear to inherit again".
          edit: CELL_COL[f.field]
            ? {
                type: CELL_NUM.has(f.field) ? ("number" as const) : ("text" as const),
                disabled: (row: Row) => isRetired(row),
                placeholder: (row: Row) => {
                  const v = effValue(row as QcParamRow, k);
                  return v ? String(v) : undefined;
                },
                // A retired row is read-only, so it shows what the column says
                // about it — including the grey inherited value — rather than
                // the editor's bare resting text.
                renderWhenDisabled: true,
              }
            : undefined,
        } as AppColumn<Row>;
      }),
    ];
  }, [selStat]);

  /* ── "All QC types (matrix)" ───────────────────────────────────────────────
     The prototype's second QC Limits view (v12): parameters down the side, QC
     types across the top, and under each type ONLY the limits its role is
     checked against — QC_KIND[kind].req. A Blank shows UCLimit, a spike shows
     SpikeAmount / %Rec, a spike duplicate adds RPDCtrlLimit. Everything else a
     flat sheet would show is noise the role never reads. */
  /** The ↓ in a matrix column header: fill that column for that QC type. It
   *  prompts for the value and posts to the SAME qc-parameters/fill endpoint the
   *  Fill bar uses, with the column and QC type already chosen. */
  const fillMatrixColumn = async (qcTypeId: string, qcTypeName: string, header: string) => {
    const value = await prompt({
      title: `Fill ${header} — ${qcTypeName}`,
      message: `Sets ${header} on every parameter of ${qcTypeName}. Single cells can still be changed afterwards.`,
      placeholder: header,
    });
    if (value === null) return;   // cancelled
    try {
      const { data } = await api.post(`${base}/qc-parameters/fill`, {
        column: header, value, qcTypeOid: qcTypeId,
      });
      const n = data?.data?.changed ?? 0;
      notify.success(n === 0 ? "No change — those rows already match." : `Updated ${n} row${n === 1 ? "" : "s"}.`);
      reload();
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
          "Could not fill the column.",
      );
    }
  };

  /** The matrix is the SAME QC rows as the per-type pane, so its cells are
   *  editable too — the prototype edits them through its own `data-m` handler
   *  (v12:1050). Its column field is "<qcTypeId>__<field>" and the QC row's own
   *  Oid rides on the row as "<qcTypeId>__oid", because one matrix ROW is a
   *  PARAMETER spanning several QC rows. A cell under a QC type that does not
   *  carry the parameter has no Oid, so it stays read-only — the prototype
   *  offers "+ add" there, which AI does not have yet. */
  const onMatrixCell = useCallback(
    (row: Row, c: AppColumn<Row>, v: string) => {
      const cut = c.field.indexOf("__");
      if (cut < 0) return;
      const typeId = c.field.slice(0, cut);
      const field = c.field.slice(cut + 2);
      const column = CELL_COL[field];
      const oid = String(row[`${typeId}__oid`] ?? "");
      if (!column || !oid) return;
      void commitCell(oid, column, v);
    },
    [commitCell],
  );

  /** The matrix's per-cell "+ add" (prototype v12:767 maddp): put THIS
   *  parameter on THIS QC type, nothing else. It posts to the SAME
   *  qc-parameters/from-sample endpoint "+ Add missing sample parameters" uses,
   *  naming one sample parameter — the two are the same copy at two scopes, so
   *  there is no second way to create a QC row (standing rule 8), and the new
   *  row inherits the Sample row's reporting and rounding exactly as the bulk
   *  add does. The prototype's own maddp seeds a BLANK row; inheriting is
   *  strictly more useful and is what AI already did for the bulk case. */
  const addOneToQcType = async (qcTypeId: string, qcTypeName: string, sampleOid: string, parameter: string) => {
    try {
      await api.post(`${base}/qc-parameters/from-sample`, {
        qcTypeOids: [qcTypeId], parameterOids: [sampleOid],
      });
      notify.success(`${parameter} added to ${qcTypeName}.`);
      reload();
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
          "Could not add that parameter.",
      );
    }
  };

  const matrixCols = useMemo((): AppColumn<Row>[] => {
    const cols: AppColumn<Row>[] = [
      { field: "parameter", header: "Parameter", width: 180 },
      { field: "rl", header: "RL", width: 80 },
    ];
    for (const t of qcTypeStats) {
      let first = true;
      for (const k of QC_KIND[t.kind].req) {
        const f = QC_FIELD[k]!;
        const isFirst = first;
        first = false;
        // Only the 11 columns AI_TestMethod_QcFill_SP writes get an arrow; the
        // rest would hand the user a control the API refuses.
        const fillable = FILL_COLS.some(([k]) => k === f.header);
        cols.push({
          field: `${t.id}__${f.field}`,
          headerText: f.header,
          header: fillable ? (
            <span className="tc-mfill-h">
              {f.header}
              <button
                type="button"
                className="tc-mfill"
                title={`Fill ${f.header} on every parameter of ${t.name}`}
                onClick={(e) => { e.stopPropagation(); void fillMatrixColumn(t.id, t.name, f.header); }}
              >
                ↓
              </button>
            </span>
          ) : f.header,
          width: 110,
          // Typed where it is read (prototype v12:768). A band whose QC type
          // does not carry this parameter has no row to write to, so that cell
          // stays read-only rather than silently discarding what was typed.
          edit: CELL_COL[f.field]
            ? {
                type: CELL_NUM.has(f.field) ? ("number" as const) : ("text" as const),
                disabled: (r: Row) => !String(r[`${t.id}__oid`] ?? ""),
                placeholder: (r: Row) => {
                  const inh = r[`${t.id}__${f.field}__inh`];
                  return inh ? String(inh) : undefined;
                },
                // A cell with no QC row behind it draws "+ add" instead of an
                // editor — see render below.
                renderWhenDisabled: true,
              }
            : undefined,
          // An inherited value reads grey italic here too, so the matrix says
          // the same thing about a cell as the per-type pane does.
          render: (row: Row) => {
            // No QC row on this type: the prototype draws a dashed "+ add"
            // spanning the band (v12:767). AppDataGrid has no per-cell colspan,
            // so it sits in the band's FIRST column and the rest stay blank —
            // one affordance per band, which is what the prototype's colspan
            // amounts to visually.
            if (!String(row[`${t.id}__oid`] ?? "")) {
              return isFirst ? (
                <button
                  type="button"
                  className="tc-addslot"
                  title={`Add ${String(row.parameter ?? "")} to ${t.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    void addOneToQcType(t.id, t.name, String(row.oid ?? ""), String(row.parameter ?? ""));
                  }}
                >
                  + add
                </button>
              ) : "";
            }
            const v = row[`${t.id}__${f.field}`];
            const inh = row[`${t.id}__${f.field}__inh`];
            if (v !== null && v !== undefined && String(v).trim() !== "") return String(v);
            return inh ? <span className="tc-qc-inh">{String(inh)}</span> : "";
          },
        });
      }
    }
    return cols;
    // fillMatrixColumn is stable for a given test; listing it would rebuild the
    // columns on every render without changing them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qcTypeStats]);

  /** One band per QC type, plus a leading unlabelled band over Parameter + RL. */
  const matrixBands = useMemo(
    () => [
      { label: "", span: 2 },
      ...qcTypeStats.map((t) => ({ label: t.name, span: QC_KIND[t.kind].req.length })),
    ],
    [qcTypeStats],
  );

  const matrixRows = useMemo((): Row[] => {
    // Index the QC rows once: (qcTypeId, parameter) -> row.
    const at = new Map<string, QcParamRow>();
    for (const r of qcParams.rows as QcParamRow[]) {
      at.set(`${String(r.qcTypeId ?? "")}|${rowKey(r)}`, r);
    }
    const seen = new Set<string>();
    return sample.rows
      .filter((sp) => {
        if (isRetired(sp) || seen.has(rowKey(sp))) return false;
        seen.add(rowKey(sp));
        return true;
      })
      .map((sp) => {
        const key = rowKey(sp);
        const row: Row = { oid: sp.oid, parameter: sp.parameter, rl: sp.rl };
        for (const t of qcTypeStats) {
          const qc = at.get(`${t.id}|${key}`);
          // The QC row behind this band, so a typed cell knows what to write.
          row[`${t.id}__oid`] = qc?.oid ?? "";
          for (const k of QC_KIND[t.kind].req) {
            const f = QC_FIELD[k]!;
            row[`${t.id}__${f.field}`] = qc ? qc[f.field] : "";
            // effValue falls back to the value inherited from the sample
            // parameter, which is what the grey italic cell shows.
            row[`${t.id}__${f.field}__inh`] = qc ? effValue(qc, k) : "";
          }
        }
        return row;
      });
  }, [sample.rows, qcParams.rows, qcTypeStats]);

  /** The sample parameters this QC type does not carry yet — what "+ Add
   *  missing sample parameters" would add (prototype missingRowsIn). */
  const missingParams = useMemo(() => {
    if (!selStat) return [] as Row[];
    const have = new Set(selStat.rows.map((r) => rowKey(r)));
    const seen = new Set<string>();
    return sample.rows.filter((r) => {
      if (isRetired(r) || have.has(rowKey(r)) || seen.has(rowKey(r))) return false;
      seen.add(rowKey(r));
      return true;
    });
  }, [selStat, sample.rows]);

  /** Add them through the SAME endpoint "Copy QC parameters from same test"
   *  uses — it already adds only what the QC type is missing (standing rule 8). */
  /** Remove the ticked QC rows. Scoped to the rows the PANE is showing, so a
   *  tick left behind on another QC type cannot be swept up by it. */
  const removeFromType = async () => {
    const oids = [...roSel].filter((x) => paneRows.some((r) => String(r.oid) === x));
    if (!oids.length) { notify.warning("Select at least one row."); return; }
    if (await removeWithEscalation(oids, paneRows as Row[], "row")) return;
    const ok = await confirm({
      title: "Remove",
      tone: "danger",
      message: `Remove ${oids.length} row${oids.length === 1 ? "" : "s"} from ${selStat?.name ?? "this QC type"}? A row attached to a sample cannot be removed - retire it instead.`,
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`${tcBase}/parameters/remove`, { oids });
      const n = data?.data?.changed ?? oids.length;
      notify.success(n === 0 ? "No change." : `Removed ${n} row${n === 1 ? "" : "s"}.`);
      setRoSel(new Set());
      reload();
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          "Could not remove the rows.",
      );
    }
  };

  /** "Test limits" opens the rounding tool on the tab that matches this QC
   *  type's KIND — a Blank opens Blank, a Duplicate opens RPD, a spike opens
   *  Recovery, and a spike duplicate opens RPD (the prototype's own mapping,
   *  v12 line 995: blank→blank, dup→rpd, everything else→rec). */
  const [limitsOpen, setLimitsOpen] = useState(false);
  const limitsKind: "blank" | "rpd" | "rec" | null = selStat
    ? selStat.kind === "blank" ? "blank"
    : selStat.kind === "dup" ? "rpd"
    : selStat.kind === "spdup" ? "rpd"
    : "rec"
    : null;

  const addMissingToType = async () => {
    if (!selStat || !missingParams.length) return;
    const ok = await confirm({
      title: "Add missing sample parameters",
      message: `Add ${missingParams.length} parameter${missingParams.length === 1 ? "" : "s"} to ${selStat.name}?`,
    });
    if (!ok) return;
    try {
      await api.post(`${base}/qc-parameters/from-sample`, { qcTypeOids: [selStat.id] });
      notify.success("Saved successfully.");
      reload();
    } catch {
      notify.error("Could not add the parameters.");
    }
  };

  /** The rows in view — the filter narrows the grid AND what Apply will touch,
   *  so what the user is looking at is what they change. */
  const qcRows = useMemo(
    () => (qcFilter ? qcParams.rows.filter((r) => String(r.qcTypeId ?? r.qcType ?? "") === qcFilter) : qcParams.rows),
    [qcParams.rows, qcFilter],
  );

  const qcdExportRows = useMemo(
    () => qcRows.map((r) => QC_COLS.map((c) => String(r[c.field] ?? ""))),
    [qcRows],
  );

  /** `filter` is passed by the By QC type pane, which fills the type it is showing.
   *  It is an argument because setting qcFilter first would not take effect until
   *  the next render, and this would then fill the PREVIOUS type. */
  const applyFill = async (filter: string = qcFilter) => {
    const rows = filter
      ? qcParams.rows.filter((r) => String(r.qcTypeId ?? r.qcType ?? "") === filter)
      : qcParams.rows;
    if (!rows.length) {
      notify.warning("There are no QC rows to fill.");
      return;
    }
    const scope = filter
      ? qcTypeOptions.find((o) => o.id === filter)?.name ?? "that QC type"
      : "every QC type";
    const ok = await confirm({
      title: "Apply to all rows",
      message: `Set ${fillCol} to "${fillVal}" on ${rows.length} QC row${rows.length === 1 ? "" : "s"} (${scope})? Rows already holding that value are left alone.`,
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`${base}/qc-parameters/fill`, {
        column: fillCol,
        value: fillVal,
        // Only send a QC type when the filter names one; the server treats null
        // as "every QC type on this test".
        qcTypeOid: filter && /^[0-9a-f-]{36}$/i.test(filter) ? filter : null,
      });
      const n = data?.data?.changed ?? 0;
      notify.success(n === 0 ? "No change — those rows already match." : `Updated ${n} row${n === 1 ? "" : "s"}.`);
      reload();
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
          "Could not apply the value.",
      );
    }
  };

  const removeComponent = async () => {
    if (!compSel) return;
    const ok = await confirm({
      title: "Remove",
      tone: "danger",
      message: `Remove the component "${compSel.component ?? ""}"? Its parameters stay on the test — they just stop being grouped under it.`,
    });
    if (!ok) return;
    try {
      await api.delete(`${tcBase}/components/${compSel.oid}`);
      notify.success("Deleted.");
      setRoSel(new Set());
      reload();
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
          "Could not remove the component.",
      );
    }
  };

  const runOp = async (
    label: string,
    fn: () => Promise<{ data?: { data?: { affected?: number; rows?: { cascadedQcRows?: number }[] } } }>,
  ) => {
    try {
      const res = await fn();
      const n = res.data?.data?.affected ?? 0;
      // Retire and Reactivate carry the parameter's QC limit rows with them,
      // so the message says how many went - otherwise rows change silently.
      const qc = res.data?.data?.rows?.[0]?.cascadedQcRows ?? 0;
      const also = qc ? ` ${qc} QC limit row${qc === 1 ? "" : "s"} followed.` : "";
      notify.success(n === 0 ? "No change." : `${label} ${n} row${n === 1 ? "" : "s"}.${also}`);
      setSel(new Set());
      reload();
    } catch (e: unknown) {
      // The API turns the SP refusals into 400 with their own message - notably
      // legacy's verbatim "The testparameter cannot be deleted! it is attached
      // to a sample." Show that, not a generic failure.
      notify.error(
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? `Could not ${label.toLowerCase()}.`,
      );
    }
  };

  /** Remove, with the prototype's IN-USE ESCALATION (v12:984 rmRows).
   *
   *  A Testparameter a real sample references cannot be deleted — legacy
   *  refuses it and AI_TestMethod_ParamOps refuses the WHOLE batch, because a
   *  partial delete would leave the user guessing which half went. That
   *  refusal names a COUNT, not the rows, and leaves the user to find Retire
   *  themselves.
   *
   *  So the split happens HERE, before anything is sent: the rows already carry
   *  `inUse` from AI_TestMethod_Parameters (both projections), so no extra
   *  round trip. Unused rows are removed; the in-use ones are carried into the
   *  Retire dialog, which is the action that actually works for them. The
   *  server guard stays exactly as it is — this is the helpful path, not the
   *  boundary (§14b.5).
   *
   *  Returns true when it has taken over, so the caller stops. */
  const removeWithEscalation = async (
    oids: string[], rows: Row[], what: string,
  ): Promise<boolean> => {
    const by = new Map(rows.map((r) => [String(r.oid ?? ""), r]));
    const nameOf = (o: string) => String(by.get(o)?.parameter ?? by.get(o)?.qcType ?? o);
    const used = oids.filter((o) => !!by.get(o)?.inUse);
    if (!used.length) return false;
    const unused = oids.filter((o) => !used.includes(o));

    const ok = await confirm({
      title: "In use — cannot remove",
      confirmLabel: "Retire instead",
      message:
        `${used.length} of the selected rows are attached to samples: ${used.map(nameOf).join(", ")}. ` +
        "Removing them would break the history of samples and reports that already use them. " +
        "Retire them instead: they stay on existing records, but can no longer be picked for new samples." +
        (unused.length
          ? ` The other ${unused.length} unused row${unused.length === 1 ? "" : "s"} will be removed.`
          : ""),
    });
    if (!ok) return true;   // cancelled — handled, nothing sent

    // The unused ones go first, exactly as the prototype does, so one pass
    // clears a mixed selection instead of two.
    if (unused.length) {
      try {
        await api.post(`${tcBase}/parameters/remove`, { oids: unused });
        notify.success(`Removed ${unused.length} ${what}${unused.length === 1 ? "" : "s"}.`);
      } catch (e: unknown) {
        notify.error(
          (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
            "Could not remove the unused rows.",
        );
        return true;
      }
    }
    setSel(new Set());
    setRoSel(new Set());
    setRetireOpen({ reactivate: false, oids: used, names: used.map(nameOf), inUse: true });
    return true;
  };

  const doRemove = async () => {
    if (!selectedInTab.length) { notify.warning("Select at least one row."); return; }
    if (await removeWithEscalation(selectedInTab, activeRows, "row")) return;
    const ok = await confirm({
      title: "Remove",
      tone: "danger",
      message: `Remove ${selectedInTab.length} row${selectedInTab.length === 1 ? "" : "s"} from this test? A row attached to a sample cannot be removed - retire it instead.`,
    });
    if (!ok) return;
    await runOp("Removed", () => api.post(`${tcBase}/parameters/remove`, { oids: selectedInTab }));
  };

  /** Retire and Reactivate act on the ticked rows that are in the right STATE,
   *  exactly as the prototype does (v12:980-981 retireRows / reactRows): Retire
   *  takes the active ones, Reactivate the retired ones, and a selection
   *  holding none of them is refused by name rather than opening a dialog that
   *  could not do anything.
   *
   *  ⚠ Both buttons used to open on ANY tick, so Reactivate on a live row
   *  offered "1 row selected · The parameter becomes selectable again" about a
   *  parameter that was already selectable (owner report 2026-10-30). A MIXED
   *  tick acts on the matching subset, as the prototype does — "tick all,
   *  reactivate" means "reactivate the retired ones", not an error. */
  const openRetireFor = async (reactivate: boolean) => {
    const want = activeRows.filter(
      (r) => selectedInTab.includes(String(r.oid)) && (reactivate ? !!r.retireDate : !isRetired(r)),
    );
    if (!want.length) {
      await alert(
        reactivate
          ? {
              title: "Nothing to reactivate",
              message: 'Tick retired rows first (use "Show retired" to see them).',
              tone: "warning",
            }
          : {
              title: "Nothing selected",
              message: "Tick the active rows to retire first.",
              tone: "warning",
            },
      );
      return;
    }
    const names = want.map((r) => String(r.parameter ?? r.qcType ?? r.oid));
    const oids = want.map((r) => String(r.oid));

    if (reactivate) {
      // The prototype's own question, verbatim (v12:982).
      const ok = await confirm({
        title: "Reactivate",
        message: `Reactivate ${names.join(", ")}? They can be picked for new samples and batches again.`,
      });
      if (!ok) return;
      await runOp("Reactivated", () =>
        api.post(`${tcBase}/parameters/retire`, {
          oids,
          retireDate: new Date().toISOString().slice(0, 10),
          reactivate: true,
          reason: "",
        }),
      );
      return;
    }

    setRetireOpen({ reactivate, oids, names });
  };

  /** The buttons alone, as a fragment. Sample Parameters renders them INSIDE its
   *  own single bar (next to the Show chips, as the prototype does), so they
   *  cannot be wrapped in a bar of their own there. */
  const rowActionButtons = kind && (
    <>
      <button className="ca-btn" onClick={() => setAddOpen(true)} disabled={viewOnly}><span className="tb-i tb-i-green">＋</span> Add</button>
      <button className="ca-btn" onClick={() => void doRemove()} disabled={viewOnly || !selectedInTab.length}><span className="tb-i tb-i-red">−</span> Remove</button>
      {/* Sample Parameters only: the tool previews a SAMPLE result, and the rule
          it reads (RL · SigFig · CutOff · Decimal) lives on those rows. */}
      {pdTab === "sp" && (
        <button
          className="ca-btn"
          onClick={() => setRoundOpen(true)}
          disabled={viewOnly || !sample.rows.length}
          title={sample.rows.length
            ? "Type raw results and see exactly what will be reported"
            : "Add a Sample Parameter first"}
        >
          <span className="tb-i tb-i-blue">👁</span> Test rounding
        </button>
      )}
      <button className="ca-btn" onClick={() => void openRetireFor(false)} disabled={viewOnly || !selectedInTab.length}><span className="tb-i tb-i-grey">↺</span> Retire</button>
      <button className="ca-btn" onClick={() => void openRetireFor(true)} disabled={viewOnly || !selectedInTab.length}><span className="tb-i tb-i-green">↻</span> Reactivate</button>
      {/* The shared ExportMenu over the VISIBLE columns, so an export matches
          what the chips are showing rather than always dumping all 27. */}
      <ExportMenu
        filename={`test-${kind?.toLowerCase() ?? "parameters"}`}
        headers={activeCols.map((c) => String(c.header ?? c.field))}
        rows={activeRows.map((r) => activeCols.map((c) => String(r[c.field] ?? "")))}
      />
      <span className="sd-child-foot" style={{ border: 0, padding: 0, marginLeft: 6 }}>{selectedInTab.length ? `${selectedInTab.length} selected` : ""}</span>
    </>
  );

  /** Surrogates / Internal Standards have no chips, so their buttons get the
   *  ordinary child bar. */
  const rowActions = kind && <div className="sd-child-bar">{rowActionButtons}</div>;



  /* ── Method Defaults toolbars ──────────────────────────────────────────────
     One bar per tab, because the three tabs store different things — see the
     table in TestCodeMethodDefaults.tsx. `Export to` is not repeated: the
     shared grid header already carries Export + Maximize (§18.6).            */

  const mdRow = <T extends Row>(rows: T[]): T | undefined => {
    const [only] = [...mdSel];
    return only ? rows.find((r) => String(r.oid ?? r.id) === only) : undefined;
  };
  const pickMdTab = (k: "qc" | "guide" | "prep") => {
    setMdTab(k);
    setMdSel(new Set());
  };

  /** One row must be ticked for Edit / Delete; say so rather than doing nothing. */
  const needOne = (what: string) => {
    notify.warning(`Tick the ${what} row to act on first.`);
    return false;
  };

  const removeQcTypes = async () => {
    const ids = [...mdSel].filter((x) => qcTypes.rows.some((r) => String(r.oid ?? r.id) === x));
    if (!ids.length) return needOne("QC type");
    const ok = await confirm({
      title: "Remove QC Types",
      tone: "danger",
      message: `Remove ${ids.length} QC type${ids.length === 1 ? "" : "s"} from this test? Its QC limit rows stay in the database and come back if you add the type again.`,
    });
    if (!ok) return false;
    try {
      await api.post(`${base}/qc-types/unlink`, { qcTypeIds: ids });
      notify.success(`Removed ${ids.length} QC type${ids.length === 1 ? "" : "s"}.`);
      setMdSel(new Set());
      reload();
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
          "Could not remove the QC types.",
      );
    }
    return true;
  };

  const deleteGuide = async () => {
    const row = mdRow(guides.rows);
    if (!row) return needOne("container / preservation");
    const ok = await confirm({
      title: "Delete",
      tone: "danger",
      message: `Delete the ${row.container ?? "container"} / ${row.preservative ?? "preservative"} row?`,
    });
    if (!ok) return false;
    try {
      await api.delete(`${base}/test-guide/${row.oid ?? row.id}`);
      notify.success("Deleted.");
      setMdSel(new Set());
      reload();
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
          "Could not delete the row.",
      );
    }
    return true;
  };

  const deletePrep = async () => {
    const row = mdRow(preps.rows);
    if (!row) return needOne("prep method");
    const ok = await confirm({
      title: "Delete",
      tone: "danger",
      message: `Delete the prep step "${row.method ?? ""}"? A step already used on samples cannot be deleted — set a Retire Date instead.`,
    });
    if (!ok) return false;
    try {
      await api.delete(`${tcBase}/prep-methods/${row.oid}`);
      notify.success("Deleted.");
      setMdSel(new Set());
      reload();
    } catch (e: unknown) {
      // The API answers 409 with the reason written for the person who clicked
      // Delete ("already used on samples"). Show that, not a generic failure.
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
          "Could not delete the prep method.",
      );
    }
    return true;
  };

  const mdBar = (() => {
    if (mdTab === "qc")
      return (
        <div className="sd-child-bar">
          <button className="ca-btn" onClick={() => setQcAddOpen(true)} disabled={viewOnly}><span className="tb-i tb-i-green">＋</span> Add</button>
          <button className="ca-btn" onClick={() => void removeQcTypes()} disabled={viewOnly || !mdSel.size}><span className="tb-i tb-i-red">−</span> Remove</button>
          <ExportMenu filename="test-qc-types" {...gridExportMatrix(qcTypes.rows, [...MD_COLS.qc])} />
          <span className="sd-child-foot" style={{ border: 0, padding: 0, marginLeft: 6 }}>
            {mdSel.size ? `${mdSel.size} selected` : ""}
          </span>
        </div>
      );
    if (mdTab === "guide")
      return (
        <div className="sd-child-bar">
          <button className="ca-btn" onClick={() => setGuideDlg({ row: null })} disabled={viewOnly}><span className="tb-i tb-i-green">＋</span> New</button>
          <button
            className="ca-btn"
            disabled={viewOnly || mdSel.size !== 1}
            onClick={() => {
              const row = mdRow(guides.rows);
              if (row) setGuideDlg({ row: row as GuideRow });
            }}
          >
            <span className="tb-i tb-i-blue">✎</span> Edit
          </button>
          <button className="ca-btn" onClick={() => void deleteGuide()} disabled={viewOnly || mdSel.size !== 1}><span className="tb-i tb-i-red">🗑</span> Delete</button>
          <ExportMenu filename="test-guide" {...gridExportMatrix(guides.rows, [...MD_COLS.guide])} />
        </div>
      );
    return (
      <div className="sd-child-bar">
        <button className="ca-btn" onClick={() => setPrepDlg({ row: null })} disabled={viewOnly}><span className="tb-i tb-i-green">＋</span> New</button>
        <button
          className="ca-btn"
          disabled={viewOnly || mdSel.size !== 1}
          onClick={() => {
            const row = mdRow(preps.rows);
            if (row) setPrepDlg({ row: row as PrepRow });
          }}
        >
          <span className="tb-i tb-i-blue">✎</span> Edit
        </button>
        <button className="ca-btn" onClick={() => void deletePrep()} disabled={viewOnly || mdSel.size !== 1}><span className="tb-i tb-i-red">🗑</span> Delete</button>
        <ExportMenu filename="test-prep-methods" {...gridExportMatrix(preps.rows, [...MD_COLS.prep])} />
      </div>
    );
  })();

  /** A leading PENCIL column that opens that row's popup — the prototype's
   *  row-level Edit. Kept out of the export (it is a control, not data). */
  /** `w` narrows the pencil for a grid with few, wide data columns, where the
   *  default 30px reads as part of a gutter (Test Guide, owner request
   *  2026-10-22). Pinned, so it is px and not a share of the table. */
  /** The pencil column, or NOTHING in view mode — a column whose cells render
   *  null still reserves its width and reads as a stray empty gutter. Spread
   *  this into a column list rather than including editPencil directly. */
  const pencilCol = (open: (row: Row) => void, w = 30): AppColumn<Row>[] =>
    viewOnly ? [] : [editPencil(open, w)];

  const editPencil = (open: (row: Row) => void, w = 30): AppColumn<Row> => ({
    field: "__edit", header: "", width: w, minWidth: w, maxWidth: w,
    sortable: false, filterable: false,
    noExport: true, text: () => "",
    // The pencil opens the row's edit dialog, so it is hidden outright in VIEW
    // mode rather than shown disabled: a greyed pencil on every row is a lot of
    // dead furniture, and the column carries nothing else.
    render: (r) => viewOnly ? null : (
      <button
        type="button"
        className="adg-rowedit-btn"
        title="Edit"
        aria-label="Edit"
        onClick={(e) => { e.stopPropagation(); open(r); }}
      >
        ✎
      </button>
    ),
  });

  /** Retired rows are hidden until the footer chip is switched on, and the
   *  chip only appears when there are any. One helper for the three grids. */
  const retiredOf = (rows: Row[]) => rows.filter((r) => isRetired(r)).length;
  /* Retired rows come LAST, in their own order (owner 2026-10-21). The
     prototype's sample data happens to end with its one retired parameter; on
     real data they interleave by Sort — PFAS EPA 1633 has them at 29, 38 and 40
     — so switching the chip on scattered three rows across three pages. Grouped
     at the end they arrive together, and the active list above them does not
     shift. A header click still sorts the whole grid, as it should. */
  const visibleRows = (rows: Row[]) =>
    revealed(rows)
      ? [...rows.filter((r) => !isRetired(r)), ...rows.filter((r) => isRetired(r))]
      : rows.filter((r) => !isRetired(r));

  /** A retired row reads GREY and carries a "Retired <date>" tag beside its
   *  name, as the prototype draws it (retTag + tr.retired, v12:641/151) — once
   *  the rows are shown they must be tellable apart from the live ones, and
   *  RetireDate itself sits in the "Other" column group, which is off by
   *  default. The date goes through the shared fmtLocal* helper (§18.6). */
  const withRetiredCue = (cols: AppColumn<Row>[], nameField: string): AppColumn<Row>[] =>
    cols.map((c) =>
      c.field === nameField
        ? {
            ...c,
            cellClass: (r: Row) => (isRetired(r) ? "tc-retired-cell" : undefined),
            render: (r: Row) => (
              <>
                {String(r[nameField] ?? "")}
                {r.retireDate ? (
                  <span className="tc-ret-tag">{isRetired(r) ? "Retired" : "Retires"} {fmtLocalDate(String(r.retireDate))}</span>
                ) : null}
              </>
            ),
          }
        : { ...c, cellClass: (r: Row) => (isRetired(r) ? "tc-retired-cell" : undefined) },
    );

  /** The prototype's footer for a grid that can hold retired rows. */
  const retiredFoot = (rows: Row[]) => {
    const ret = retiredOf(rows);
    // Nothing retired, nothing to say: the grid's own footer already carries
    // the row count, so this strip would only repeat it.
    if (ret === 0) return null;
    const show = revealed(rows);
    return (
      <div className="sd-static-note sd-child-foot tc-retfoot">
        <span>
          Count = {rows.length - ret} active · {ret} retired
        </span>
        <button
          type="button"
          className={`tc-chip${show ? " on" : ""}`}
          onClick={() => toggleRetired(rows)}
        >
          {show ? `Hide retired (${ret})` : `Show retired (${ret})`}
        </button>
      </div>
    );
  };

  /** Row-selection wiring shared by the three Method Defaults grids. */
  const mdSelect = {
    selectable: true as const,
    selectedIds: mdSel as Set<string | number>,
    onSelectionChange: (ids: Iterable<string | number>) => setMdSel(new Set([...ids].map(String))),
  };

  const tab = <T extends string>(cur: T, k: T, label: string, n: number | string, set: (v: T) => void) => (
    <button
      key={k}
      type="button"
      role="tab"
      aria-selected={cur === k}
      className={`acc-tab${cur === k ? " active" : ""}`}
      onClick={() => set(k)}
    >
      {label} <span className="tc-tab-n">{n}</span>
    </button>
  );

  // A group test replaces both cards with its member list.
  if (isGroup) return <TestCodeGroupTest base={base} testName={testName} />;

  return (
    <div className={viewOnly ? "tc-tabs tc-ro" : "tc-tabs"}>
      <div className="sd-section" id="tc-sec-md">
        {/* Same shared heading as "General Information" above, so the three
            cards read as one form (the .tc-detail rule keeps it normal case). */}
        <h3 className="sd-section-h">Method Defaults</h3>
        <div className="acc-tabs" role="tablist">
          {tab(mdTab, "qc", "QC Types", qcTypes.rows.length, pickMdTab)}
          {tab(mdTab, "guide", "Test Guide", guides.rows.length, pickMdTab)}
          {tab(mdTab, "prep", "Prep Method", preps.rows.length, pickMdTab)}
        </div>
        {mdBar}
        {mdTab === "qc" && (
          <>
          <AppDataGrid
            rows={qcTypes.rows}
            loading={qcTypes.loading}
            {...mdSelect}
            columns={qcTypeCols}
            onCellCommit={(r, c, v) => void onQcTypeCell(r, c, v)}
            fixedLayout
            getRowId={(r, i) => String(r.oid ?? r.id ?? i)}
            emptyText="No QC types. Click + Add."
            maximizable
            exportFilename="test-qc-types"
            alwaysShowFooter
          />
          </>
        )}
        {mdTab === "guide" && (
          <>
          <AppDataGrid
            rows={guides.rows}
            loading={guides.loading}
            {...mdSelect}
            /* Container · Preservative · Temperature only (owner request 2026-10-15,
               prototype v12). The three holding-time columns moved UP to General
               Information, where the test carries one value for all its rows;
               MonitorHoldTime and WarnBefore (h) were dropped from the UI with
               them (owner decision) - the columns still exist on dbo.TestGuide and
               are left untouched by every write here. */
            columns={[...pencilCol((r) => setGuideDlg({ row: r as GuideRow }), 22), ...MD_COLS.guide]}
            /* Three wide data columns, so the standard 34 + 30 control pair
               read as a gutter before Container (owner request 2026-10-22).
               Scoped to THIS grid - the shared defaults are untouched. */
            checkboxWidth={24}
            fixedLayout
            getRowId={(r, i) => String(r.oid ?? r.id ?? i)}
            onRowDoubleClick={(r) => setGuideDlg({ row: r as GuideRow })}
            emptyText="No container / preservation guide."
            maximizable
            exportFilename="test-guide"
            alwaysShowFooter
          />
          </>
        )}
        {mdTab === "prep" && (
          <>
          <AppDataGrid
            rows={preps.rows}
            loading={preps.loading}
            {...mdSelect}
            columns={[...pencilCol((r) => setPrepDlg({ row: r as PrepRow })), ...MD_COLS.prep]}
            fixedLayout
            getRowId={(r, i) => String(r.oid ?? i)}
            onRowDoubleClick={(r) => setPrepDlg({ row: r as PrepRow })}
            emptyText="No prep methods — analysis runs directly on the sample."
            maximizable
            exportFilename="test-prep-methods"
            alwaysShowFooter
          />
          </>
        )}
      </div>

      <div className="sd-section" id="tc-sec-pd">
        <h3 className="sd-section-h">Test Parameter Defaults</h3>
        <div className="acc-tabs" role="tablist">
          {tab(pdTab, "sp", "Sample Parameters", sample.rows.length, pickPdTab)}
          {tab(pdTab, "surr", "Surrogates", surr.rows.length, pickPdTab)}
          {tab(pdTab, "is", "Internal Standards", istd.rows.length, pickPdTab)}
          {tab(
            pdTab,
            "qcl",
            "QC Limits",
            // The prototype's badge: the shortfall when there is one, else a
            // tick once QC types exist, else nothing.
            totalMissing ? `⚠ ${totalMissing}` : qcTypes.rows.length ? "✓" : 0,
            pickPdTab,
          )}
          {tab(pdTab, "comp", "Component", components.length, pickPdTab)}
          {tab(pdTab, "qcd", "QC Parameter Defaults", qcParams.rows.length, pickPdTab)}
        </div>

        {pdTab === "sp" && (
          <>
            {/* ONE row, in the prototype's order: the actions first, then the
                column-group chips after them (owner request 2026-10-13). They
                were two stacked rows — chips above, buttons below — which read
                as two separate toolbars and cost a row of height. */}
            <div className="sd-child-bar tc-spbar">
              {rowActionButtons}
              <span className="tc-chips-lbl">Show:</span>
              {(Object.keys(G) as (keyof typeof G)[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`tc-chip${groups.has(k) ? " on" : ""}`}
                  title={(G[k][1] as readonly string[]).map((f) => LABEL[f] ?? f).join(", ")}
                  onClick={() =>
                    setGroups((prev) => {
                      const next = new Set(prev);
                      if (next.has(k)) next.delete(k);
                      else next.add(k);
                      return next;
                    })
                  }
                >
                  {groups.has(k) ? "✓ " : ""}
                  {G[k][0]} <span className="tc-chip-n">{G[k][1].length}</span>
                </button>
              ))}
              <button
                type="button"
                className="tc-chip"
                onClick={() => setGroups(new Set(Object.keys(G) as (keyof typeof G)[]))}
              >
                All
              </button>
              <span className="tc-colcount">
                {spCols.length} of {totalCols} columns
              </span>
            </div>
            <AppDataGrid
              rows={visibleRows(sample.rows)}
              loading={sample.loading}
              selectable
              selectedIds={sel as Set<string | number>}
              onSelectionChange={(ids) => setSel(new Set([...ids].map(String)))}
              columns={editable(withRetiredCue(spCols, "parameter"))}
              onCellCommit={onCell}
              bands={spBands}
              getRowId={(r, i) => String(r.oid ?? i)}
              emptyText="No parameters."
              maximizable
              jumpToPage="last"
              jumpToken={retiredJump}
              exportFilename="test-sample-parameters"
            alwaysShowFooter
            />
            {retiredFoot(sample.rows)}
          </>
        )}

        {pdTab === "surr" && (
          <>
          {rowActions}
          <AppDataGrid
            rows={visibleRows(surr.rows)}
            loading={surr.loading}
              selectable
              selectedIds={sel as Set<string | number>}
              onSelectionChange={(ids) => setSel(new Set([...ids].map(String)))}
            columns={editable(withRetiredCue(surrCols, "parameter"))}
            onCellCommit={onCell}
            getRowId={(r, i) => String(r.oid ?? i)}
            emptyText="No surrogates."
            maximizable
            jumpToPage="last"
            jumpToken={retiredJump}
            exportFilename="test-surrogates"
            alwaysShowFooter
          />
          {retiredFoot(surr.rows)}
          </>
        )}

        {pdTab === "is" && (
          <>
          {rowActions}
          <AppDataGrid
            rows={visibleRows(istd.rows)}
            loading={istd.loading}
              selectable
              selectedIds={sel as Set<string | number>}
              onSelectionChange={(ids) => setSel(new Set([...ids].map(String)))}
            columns={editable(withRetiredCue(istdCols, "parameter"))}
            onCellCommit={onCell}
            getRowId={(r, i) => String(r.oid ?? i)}
            emptyText="No internal standards."
            maximizable
            jumpToPage="last"
            jumpToken={retiredJump}
            exportFilename="test-internal-standards"
            alwaysShowFooter
          />
          {retiredFoot(istd.rows)}
          </>
        )}

        {/* QC LIMITS — the prototype's two views (v12): "By QC type" shows a rail
            of the test's QC types and, beside it, ONLY the limit columns that
            type's KIND is checked against; "All QC types (matrix)" is the flat
            sheet of every row. The kind rules live in testCodeQc.ts, shared with
            the copy dialogs. */}
        {pdTab === "qcl" && (
          qcTypes.rows.length === 0 ? (
            <div className="tc-qc-empty">
              No QC types yet. Add them in Method Defaults › QC Types. Each new QC type
              is filled with this test&apos;s Sample Parameters automatically.
            </div>
          ) : !qcTypes.rows.some((r) => showRetired || !isRetired(r)) ? (
            <div className="tc-qc-empty">
              All QC types are retired.{" "}
              <button type="button" className="tc-chip" onClick={() => toggleRetired()}>
                Show retired
              </button>
            </div>
          ) : (
          <>
          <div className="sd-child-bar tc-qcbar">
            <span className="tc-qcview">
              <button
                type="button"
                className={`tc-qcview-chip${qcView === "type" ? " on" : ""}`}
                onClick={() => setQcView("type")}
              >
                By QC type
              </button>
              <button
                type="button"
                className={`tc-qcview-chip${qcView === "matrix" ? " on" : ""}`}
                onClick={() => setQcView("matrix")}
              >
                All QC types (matrix)
              </button>
            </span>
          </div>

          {qcView === "type" ? (
            <div className="tc-qc-split">
              <div className="tc-qc-rail">
                {qcTypeStats.map((t) => (
                  <button
                    type="button"
                    key={t.id}
                    className={`tc-qc-railitem${t.id === qcSel ? " on" : ""}${t.missing || t.notSetUp ? " warn" : ""}`}
                    onClick={() => { setQcSel(t.id); setRetiredPref(null); }}
                  >
                    <span className="tc-qc-railtop">
                      <b>{t.name}</b>
                      <span className={`tc-qc-badge k-${t.kind}`}>{QC_KIND[t.kind].label}</span>
                    </span>
                    <small>
                      {t.active}/{activeSampleCount} parameters
                      {/* "N LIMITS missing", not the prototype's bare "N missing": beside
                          "1/1 parameters" that reads as N PARAMETERS missing, which is
                          impossible at 1 of 1. The count is required limit VALUES, which is
                          also how the chip above and the tab badge word it (owner report
                          2026-10-16). One word; the number is unchanged. */}
                      {t.missing ? <> · <b className="tc-qc-bad">{t.missing} limits missing</b></> : null}
                      {!t.missing && !t.notSetUp ? <> · <b className="tc-qc-good">✓ complete</b></> : null}
                      {t.notSetUp ? <> · <b className="tc-qc-bad">{t.notSetUp} not set up</b></> : null}
                    </small>
                  </button>
                ))}
                {!qcTypeStats.length && (
                  <div className="tc-setcat-empty">
                    No QC parameters. Add QC types in Method Defaults › QC Types.
                  </div>
                )}
              </div>

              <div className="tc-qc-pane">
                {selStat && (
                  <>
                    <div className="tc-qc-panehead">
                      <b>{selStat.name}</b>
                      <span className={`tc-qc-badge k-${selStat.kind}`}>{QC_KIND[selStat.kind].label}</span>
                      <span className="tc-qc-rule">Pass: {QC_KIND[selStat.kind].rule}</span>
                    </div>
                    <div className="sd-child-bar">
                      <button
                        className="ca-btn"
                        onClick={() => void addMissingToType()}
                        disabled={viewOnly || !missingParams.length}
                        title={
                          missingParams.length
                            ? `${missingParams.length} sample parameter(s) not on this QC type`
                            : "This QC type already has every sample parameter"
                        }
                      >
                        <span className="tb-i tb-i-green">＋</span> Add missing sample parameters
                        {missingParams.length ? ` (${missingParams.length})` : ""}
                      </button>
                      <button
                        className="ca-btn"
                        onClick={() => void removeFromType()}
                        disabled={viewOnly || ![...roSel].some((x) => paneRows.some((r) => String(r.oid) === x))}
                      >
                        <span className="tb-i tb-i-red">−</span> Remove
                      </button>
                      {!viewOnly && (
                        <>
                      <button
                        className="ca-btn"
                        onClick={() => setLimitsOpen(true)}
                        disabled={!paneRows.length}
                        title="Type a value and see what this QC type would report, and whether it passes"
                      >
                        <span className="tb-i tb-i-blue">👁</span> Test limits
                      </button>
                      <span className="tc-chips-lbl">Fill</span>
                      <AiCombo
                        value={fillCol}
                        options={fillOptions}
                        onChange={(v) => setFillCol(v ?? "RL")}
                      />
                      <span className="tc-chips-lbl">=</span>
                      <input
                        className="sd-input tc-fillval"
                        value={fillVal}
                        placeholder="value"
                        onChange={(e) => setFillVal(e.target.value)}
                      />
                      <button
                        className="ca-btn"
                        onClick={() => void applyFill(selStat.id)}
                        disabled={viewOnly || !paneRows.length}
                      >
                        <span className="tb-i tb-i-green">✔</span> Apply to all
                      </button>
                        </>
                      )}
                      <ExportMenu
                        filename={`test-qc-limits-${selStat.name}`}
                        headers={kindCols.map((c) => String(c.header ?? c.field))}
                        rows={paneRows.map((r) => kindCols.map((c) => String((r as Row)[c.field] ?? "")))}
                      />
                    </div>
                    <AppDataGrid
                      rows={paneRows as Row[]}
                      loading={qcParams.loading}
                      {...roSelect}
                      columns={editable(kindCols)}
                      onCellCommit={onCell}
                      getRowId={(r, i) => String(r.oid ?? i)}
                      emptyText="No parameters on this QC type yet."
                      maximizable
                      jumpToPage="last"
                      jumpToken={retiredJump}
                      exportFilename={`test-qc-limits-${selStat.name}`}
                      alwaysShowFooter
                    />
                    {selStat.retired > 0 && (
                    <div className="sd-static-note sd-child-foot tc-retfoot">
                      <span>
                        Count = {selStat.active} active · {selStat.retired} retired
                      </span>
                      <button
                        type="button"
                        className={`tc-chip${showRetired ? " on" : ""}`}
                        onClick={() => toggleRetired()}
                      >
                        {showRetired
                          ? `Hide retired (${selStat.retired})`
                          : `Show retired (${selStat.retired})`}
                      </button>
                    </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
          {/* The Fill bar stays here: the prototype fills a matrix column from a
              ↓ in its header, which is not built, so removing this would take
              the capability away rather than move it. */}
          <div className="sd-child-bar tc-qcbar">
            {!viewOnly && (
              <>
            <span className="tc-chips-lbl">QC Types:</span>
            <AiCombo
              value={qcFilter || null}
              options={qcTypeOptions.map((o) => ({ value: o.id, label: o.name }))}
              placeholder={`All (${qcParams.rows.length})`}
              naLabel={`All (${qcParams.rows.length})`}
              onChange={(v) => setQcFilter(v ?? "")}
            />

            <span className="tc-chips-lbl">Fill</span>
            <AiCombo
              value={fillCol}
              options={FILL_COLS.map(([k, l]) => ({ value: k, label: l }))}
              onChange={(v) => setFillCol(v ?? "RL")}
            />
            <span className="tc-chips-lbl">=</span>
            <input
              className="sd-input tc-fillval"
              value={fillVal}
              placeholder="value"
              onChange={(e) => setFillVal(e.target.value)}
            />
            <button className="ca-btn" onClick={() => void applyFill()} disabled={!qcRows.length}>
              <span className="tb-i tb-i-green">✔</span> Apply to all rows
            </button>
              </>
            )}
            <ExportMenu
              filename="test-qc-parameters"
              headers={QC_COLS.map((c) => String(c.header ?? c.field))}
              rows={qcdExportRows}
            />
          </div>
          <AppDataGrid
            rows={matrixRows}
            loading={qcParams.loading || sample.loading}
            columns={matrixCols}
            onCellCommit={onMatrixCell}
            bands={matrixBands}
            getRowId={(r, i) => String(r.oid ?? i)}
            emptyText="No QC parameters. Add QC types in Method Defaults › QC Types."
            maximizable
            exportFilename="test-qc-limits-matrix"
            alwaysShowFooter
          />
            </>
          )}
          </>
          )
        )}

        {/* QC PARAMETER DEFAULTS — the same rows as QC Limits, as one sheet
            (prototype v12: "same rows as QC Limits — edit in either place"). */}
        {pdTab === "qcd" && (
          <>
          <div className="sd-child-bar tc-qcbar">
            {!viewOnly && (
              <>
            <span className="tc-chips-lbl">QC Types:</span>
            <AiCombo
              value={qcFilter || null}
              options={qcTypeOptions.map((o) => ({ value: o.id, label: o.name }))}
              placeholder={`All (${qcParams.rows.length})`}
              naLabel={`All (${qcParams.rows.length})`}
              onChange={(v) => setQcFilter(v ?? "")}
            />

            <span className="tc-chips-lbl">Fill</span>
            <AiCombo
              value={fillCol}
              options={FILL_COLS.map(([k, l]) => ({ value: k, label: l }))}
              onChange={(v) => setFillCol(v ?? "RL")}
            />
            <span className="tc-chips-lbl">=</span>
            <input
              className="sd-input tc-fillval"
              value={fillVal}
              placeholder="value"
              onChange={(e) => setFillVal(e.target.value)}
            />
            <button className="ca-btn" onClick={() => void applyFill()} disabled={!qcRows.length}>
              <span className="tb-i tb-i-green">✔</span> Apply to all rows
            </button>
              </>
            )}
            <ExportMenu
              filename="test-qc-parameters"
              headers={QC_COLS.map((c) => String(c.header ?? c.field))}
              rows={qcdExportRows}
            />
          </div>
          <AppDataGrid
            rows={qcRows}
            loading={qcParams.loading}
            {...roSelect}
            columns={editable(QC_COLS)}
            onCellCommit={onCell}
            getRowId={(r, i) => String(r.oid ?? i)}
            emptyText={qcFilter ? "No QC rows for that QC type." : "No QC parameters. Add QC types in Method Defaults › QC Types."}
            maximizable
            exportFilename="test-qc-limits"
            alwaysShowFooter
          />
          </>
        )}

        {pdTab === "comp" && (
          <>
          <div className="sd-child-bar">
            <button className="ca-btn" onClick={() => setCompDlg({ row: null })} disabled={viewOnly}><span className="tb-i tb-i-green">＋</span> Add</button>
            <button
              className="ca-btn"
              disabled={!compEdit || !!compEdit.isShared}
              title={compEdit?.isShared ? "Shared with other tests — change it where it is owned." : undefined}
              onClick={() => compEdit && setCompDlg({ row: compEdit })}
            >
              <span className="tb-i tb-i-blue">✎</span> Edit
            </button>
            <button
              className="ca-btn"
              disabled={viewOnly || !compSel || !!compSel.isShared}
              title={compSel?.isShared ? "Shared with other tests — it cannot be removed here." : undefined}
              onClick={() => void removeComponent()}
            >
              <span className="tb-i tb-i-red">−</span> Remove
            </button>
            <ExportMenu
              filename="test-components"
              headers={COMP_COLS.map((c) => String(c.header ?? c.field))}
              rows={components.map((r) => COMP_COLS.map((c) => String(r[c.field] ?? "")))}
            />
            {compSel?.isShared && (
              <span className="tc-shared-note">
                <b>{compSel.component}</b> is shared with other tests — edit or remove it
                where it is owned.
              </span>
            )}
          </div>
          <AppDataGrid
            rows={components}
            loading={comps.loading}
            {...roSelect}
            columns={[
              ...[editPencil((r) => setCompDlg({ row: r as ComponentRow }), 30)].map((c) => ({
                ...c,
                render: (r: Row) =>
                  (r as ComponentRow).isShared ? null : (
                    <button
                      type="button"
                      className="adg-rowedit-btn"
                      title="Edit"
                      aria-label="Edit"
                      onClick={(e) => { e.stopPropagation(); setCompDlg({ row: r as ComponentRow }); }}
                    >
                      ✎
                    </button>
                  ),
              })),
              ...COMP_COLS,
            ]}
            getRowId={(r, i) => String(r.oid ?? i)}
            onRowClick={(r) => setCompCur(String((r as ComponentRow).oid))}
            onRowDoubleClick={(r) => !(r as ComponentRow).isShared && setCompDlg({ row: r as ComponentRow })}
            emptyText="No components. Click + Add."
            maximizable
            exportFilename="test-components"
            alwaysShowFooter
          />
          </>
        )}
      </div>

      <QcTypeAddDialog
        open={qcAddOpen}
        base={base}
        onClose={() => setQcAddOpen(false)}
        onDone={(n) => {
          notify.success(`Added ${n} QC type${n === 1 ? "" : "s"}.`);
          setMdSel(new Set());
          reload();
        }}
      />
      {guideDlg && (
        <TestGuideDialog
          open
          base={base}
          row={guideDlg.row}
          onClose={() => setGuideDlg(null)}
          onDone={() => {
            notify.success("Saved successfully.");
            setMdSel(new Set());
            reload();
          }}
        />
      )}
      {prepDlg && (
        <PrepMethodDialog
          open
          base={base}
          row={prepDlg.row}
          // The server derives both on insert; these are the SAME formulas, shown
          // read-only so the dialog agrees with what will be stored.
          nextSort={preps.rows.length + 1}
          nextTier={preps.rows.length + 2}
          department={owner?.department}
          defaultMethodId={owner?.methodId}
          onClose={() => setPrepDlg(null)}
          onDone={() => {
            notify.success("Saved successfully.");
            setMdSel(new Set());
            reload();
          }}
        />
      )}
      {kind && (
        <AddParametersDialog
          open={addOpen}
          base={base}
          kind={kind}
          onClose={() => setAddOpen(false)}
          onDone={(n) => {
            notify.success(n === 0 ? "No change." : `Added ${n} parameter${n === 1 ? "" : "s"}.`);
            reload();
          }}
        />
      )}
      {/* "Test limits" — the same tool, opened on the QC tab for the selected
          type. It reuses TestRoundingDialog rather than forking a second one. */}
      {limitsKind && (
        <TestRoundingDialog
          open={limitsOpen}
          base={base}
          params={sample.rows.map((r) => ({
            oid: String(r.oid),
            parameter: String(r.parameter ?? ""),
            rl: r.rl == null ? null : String(r.rl),
            cutOff: r.cutOff == null ? null : String(r.cutOff),
            retired: isRetired(r),
          }))}
          initialOid={
            // The ticked QC row's parameter where there is one, else the first.
            paneRows.find((r) => roSel.has(String(r.oid)))
              ? String(
                  sample.rows.find(
                    (sp) =>
                      String(sp.parameterId ?? sp.parameter ?? "") ===
                      String(
                        paneRows.find((r) => roSel.has(String(r.oid)))?.parameterId ??
                          paneRows.find((r) => roSel.has(String(r.oid)))?.parameter ??
                          "",
                      ),
                  )?.oid ?? "",
                )
              : undefined
          }
          qcTypes={qcTypes.rows.map((t) => ({
            id: String(t.id),
            qcType: t.qcType == null ? null : String(t.qcType),
            qcRole: t.qcRole == null ? null : String(t.qcRole),
            qcRootRole: t.qcRootRole == null ? null : String(t.qcRootRole),
            qcSource: t.qcSource == null ? null : String(t.qcSource),
          }))}
          testName={testName}
          initialKind={limitsKind}
          initialQcTypeId={selStat?.id}
          onClose={() => setLimitsOpen(false)}
        />
      )}
      <TestRoundingDialog
        open={roundOpen}
        base={base}
        params={sample.rows.map((r) => ({
          oid: String(r.oid),
          parameter: String(r.parameter ?? ""),
          rl: r.rl == null ? null : String(r.rl),
          cutOff: r.cutOff == null ? null : String(r.cutOff),
          retired: isRetired(r),
        }))}
        // The four tabs are one tool, so the Sample Parameters entry point offers
        // the QC tabs too — they just open with no type picked yet.
        qcTypes={qcTypes.rows.map((t) => ({
          id: String(t.id),
          qcType: t.qcType == null ? null : String(t.qcType),
          qcRole: t.qcRole == null ? null : String(t.qcRole),
          qcRootRole: t.qcRootRole == null ? null : String(t.qcRootRole),
          qcSource: t.qcSource == null ? null : String(t.qcSource),
        }))}
        testName={testName}
        initialOid={[...sel].find((x) => sample.rows.some((r) => String(r.oid) === x))}
        onClose={() => setRoundOpen(false)}
      />
      {compDlg && (
        <TestComponentDialog
          open
          base={base}
          row={compDlg.row}
          viewOnly={viewOnly}
          /* Keyed on the PARAMETER, not the row. A parameter has its own
             Testparameter row per component, so this list's row Oid and a
             member's row Oid are different values for the same analyte — which
             is why the Add picker used to offer parameters the component
             already had (owner report 2026-10-27). */
          params={sample.rows.map((r) => ({
            oid: String(r.parameterId ?? r.oid),
            parameter: String(r.parameter ?? ""),
          }))}
          onClose={() => setCompDlg(null)}
          onDone={() => {
            notify.success("Saved successfully.");
            setRoSel(new Set());
            reload();
          }}
        />
      )}
      {retireOpen && (
        <RetireDialog
          open
          reactivate={retireOpen.reactivate}
          label={pdTab === "surr" ? "Surrogate" : pdTab === "is" ? "Internal Standard" : "Parameter"}
          usageUrl={`${tcBase}/parameters/usage`}
          oids={retireOpen.oids ?? selectedInTab}
          names={retireOpen.names}
          onClose={() => setRetireOpen(null)}
          onSubmit={async (date, reason) => {
            await runOp(retireOpen.reactivate ? "Reactivated" : "Retired", () =>
              api.post(`${tcBase}/parameters/retire`, {
                oids: retireOpen.oids ?? selectedInTab,
                retireDate: date,
                reactivate: retireOpen.reactivate,
                reason,
              }),
            );
            setRetireOpen(null);
          }}
        />
      )}
    </div>
  );
}

/** Add picker — live parameters not already on this tab (the SP excludes
 *  retired ones, matching the legacy picker). */
function AddParametersDialog({
  open, base, kind, onClose, onDone,
}: {
  open: boolean; base: string; kind: string; onClose: () => void; onDone: (added: number) => void;
}) {
  // Same split as the parent: the parameters resource moved under /test-code/
  // so it stops clashing with the shared Test Method dialog's routes.
  const tcBase = `${base}/test-code`;
  const [opts, setOpts] = useState<{ id: string; name: string }[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const notify = useNotify();

  useEffect(() => {
    if (!open) return;
    setPicked(new Set()); setQ("");
    void (async () => {
      try {
        const { data } = await api.get(`${tcBase}/parameters/available`, { params: { kind } });
        setOpts(data?.data ?? []);
      } catch { setOpts([]); }
    })();
  }, [open, base, kind]);

  if (!open) return null;
  const shown = opts.filter((o) => !q.trim() || o.name.toLowerCase().includes(q.trim().toLowerCase()));

  const save = async () => {
    if (!picked.size) { notify.warning("Select at least one parameter."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`${tcBase}/parameters`, { parameterIds: [...picked], kind });
      onDone(data?.data?.affected ?? 0);
      onClose();
    } catch (e: unknown) {
      notify.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Could not add.");
    } finally { setBusy(false); }
  };

  return (
    <AppDialog open={open} title="Add Parameters" width={520} onClose={onClose} className="dlg-center"
      footer={<>
        <button className="ca-btn ca-btn-solid" onClick={() => void save()} disabled={busy}>Add{picked.size ? ` (${picked.size})` : ""}</button>
        <button className="ca-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
      </>}>
      {/* tc-dlg-input: square + plain white, like every control on this form.
          The row right-aligns it at a fixed width, rather than stretching it the
          whole dialog for a one-word filter (owner request 2026-10-30). */}
      <div className="tc-dlg-searchrow">
        <input className="sd-input tc-dlg-input" placeholder="Search parameters…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="tc-setcat-list" style={{ marginTop: 8, maxHeight: 320 }}>
        {shown.map((o) => (
          <label key={o.id}>
            <input type="checkbox" checked={picked.has(o.id)}
              onChange={() => setPicked((p) => { const n = new Set(p); n.has(o.id) ? n.delete(o.id) : n.add(o.id); return n; })} />
            {o.name}
          </label>
        ))}
        {!shown.length && <div className="tc-setcat-empty">Nothing left to add — every parameter is already on this tab.</div>}
      </div>
    </AppDialog>
  );
}

/** Retire / Reactivate. The prototype asks for a REASON; it has no column under
 *  the owner constraints, so it travels to the audit trail only. */
function RetireDialog({
  open, reactivate, label, usageUrl, oids, names, onClose, onSubmit,
}: {
  open: boolean; reactivate: boolean;
  /** The rows this is about, by name — always, so the dialog says WHICH rows it
   *  will act on rather than a bare count. */
  names?: string[];
  /** What the row is, for the title: Retire Parameter / Surrogate / Internal Standard. */
  label: string;
  usageUrl: string;
  /** The rows being retired; `names` is in the same order. */
  oids: string[];
  onClose: () => void; onSubmit: (date: string, reason: string) => Promise<void>;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const notify = useNotify();
  // "used on 1,801 results" (prototype v12 openRetire). The dialog still works
  // without it: until it arrives, or if it fails, the row shows its name alone.
  const [usage, setUsage] = useState<Map<string, { results: number; preLoginTests: number }> | null>(null);
  const oidKey = oids.join(",");
  useEffect(() => {
    if (!open || reactivate || !oidKey) return;
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.post(usageUrl, { oids: oidKey.split(",") });
        const rows = (Array.isArray(data?.data) ? data.data : []) as
          { oid: string; results: number; preLoginTests: number }[];
        if (alive) setUsage(new Map(rows.map((r) => [String(r.oid).toLowerCase(), r])));
      } catch {
        // The count is information, not a gate; the shared axios handling logged it.
        if (alive) setUsage(null);
      }
    })();
    return () => { alive = false; };
  }, [open, reactivate, usageUrl, oidKey]);
  if (!open) return null;

  const usageText = (oid: string | undefined): string => {
    const u = oid ? usage?.get(oid.toLowerCase()) : undefined;
    if (!u) return "";
    if (u.results > 0) return ` — used on ${u.results.toLocaleString("en-US")} result${u.results === 1 ? "" : "s"}`;
    if (u.preLoginTests > 0) return ` — used on ${u.preLoginTests.toLocaleString("en-US")} pre-login test${u.preLoginTests === 1 ? "" : "s"}`;
    return " — not used yet";
  };

  const go = async () => {
    if (!reactivate && (!date || !reason.trim())) {
      notify.warning("Enter the retire date and the reason.");
      return;
    }
    setBusy(true);
    try { await onSubmit(date, reason.trim()); } finally { setBusy(false); }
  };

  return (
    <AppDialog
      open={open}
      title={reactivate ? "Reactivate" : `Retire ${label}${oids.length > 1 ? "s" : ""}`}
      width={460}
      onClose={onClose}
      className="dlg-center"
      footer={<>
        <button className="ca-btn ca-btn-solid" onClick={() => void go()} disabled={busy}>{reactivate ? "Reactivate" : "Retire"}</button>
        <button className="ca-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
      </>}>
      {!!names?.length && (
        <ul className="tc-uselist">
          {/* Keyed by row: a parameter on several components repeats its name. */}
          {names.map((n, i) => <li key={oids[i] ?? n}><b>{n}</b>{usageText(oids[i])}</li>)}
        </ul>
      )}
      {!reactivate && (
        <div className="tc-copy-grid" style={{ marginTop: 8 }}>
          <label className="tc-copy-lbl">Retire date<span className="req">*</span></label>
          <input className="sd-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <label className="tc-copy-lbl">Reason<span className="req">*</span></label>
          <textarea className="sd-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
      )}
      {reactivate && (
        <div className="sd-hint">The parameter becomes selectable again for new samples.</div>
      )}
    </AppDialog>
  );
}
